export const monthlyUpdates = [
  {
    id: '2026-10-03',
    monthLabel: 'Fall 2026',
    content: `
      <div class="pulse-hero">
        <div style="margin-bottom: 10px;">
          <span style="display: inline-block; padding: 4px 14px; border-radius: 9999px; font-size: 0.75rem; font-weight: 700; text-transform: uppercase; background: rgba(255, 140, 0, 0.15); color: #ff9f43; border: 1px solid rgba(255, 159, 67, 0.3); letter-spacing: 0.8px;">
            🎃 Spooky Season 2026 🍂
          </span>
        </div>
        <h1>Fall Development Update</h1>
        <p>Systems Architecture, Connected Operations & The Living City</p>
      </div>

      <p>
        In our Summer Pulse, we talked about foundational rebuilds: inventory, civilian jobs, communications, and clothing architecture. Over the last few months, the focus shifted to connecting these systems together so the city actually reacts to what players do.
      </p>
      <p>
        Your phone now talks directly to your physical inventory. Forensics can triage confiscated devices to trace digital evidence. Civilian squads share live objectives and split paychecks evenly, and the judicial system runs on an itemized civil ledger.
      </p>
      <p>
        Here is a breakdown of what has landed across the various systems and updates.
      </p>

      <div style="margin: 24px 0 10px 0;">
        <span style="display: inline-block; padding: 3px 10px; border-radius: 4px; font-size: 0.75rem; font-weight: 700; text-transform: uppercase; background: rgba(0, 196, 204, 0.15); color: #00c4cc; letter-spacing: 0.8px;">Mobile Systems & Communications</span>
      </div>
      <h2>📱 Communications: Group Chats, Tapbacks & Carrier SIMs</h2>
      <p>
        While our phone keeps its familiar base, apps, and overall feel, the underlying communications systems have received major overhauls. The Messages app, voice calling, and cellular line management have been heavily rewritten to bring group chats, real-time tapbacks, and genuine carrier dynamics to the city.
      </p>

      <h3>💬 Group Messaging</h3>
      <p>
        You can now create full Group Chats in the Messages app for crews, businesses, and friend groups:
      </p>
      <ul>
        <li><strong>Group Profiles & Roles:</strong> Name your chat, set custom avatars, and assign Owner, Admin, and Member roles.</li>
        <li><strong>Management Controls:</strong> Admins and owners can invite contacts, remove members, or transfer ownership. Members can mute chat notifications individually.</li>
        <li><strong>Custom Wallpapers:</strong> Set custom background image URLs per conversation, saved locally on your device.</li>
      </ul>

      <h3>⚡ Tapbacks & Message Search</h3>
      <ul>
        <li><strong>Emoji Tapbacks:</strong> React instantly with real-time emoji reactions (❤️, 👍, 👎, 😂, ‼️, ❓, 🔥) in both private and group messages.</li>
        <li><strong>In-Thread Search:</strong> A search area lets you search conversation history with keyword matching, match counters, and quick filters (<code>has:image</code>, <code>from:them</code>, <code>from:me</code>).</li>
      </ul>

      <h3>📶 Physical SIM Cards & Carrier Line Protection</h3>
      <p>
        We completely rethought how phone numbers, physical SIM cards, and cellular lines work to create real roleplay stakes without letting players get griefed:
      </p>
      <ul>
        <li><strong>Physical SIM Cards:</strong> Your phone number and active carrier subscription are tied to a physical SIM card seated inside your phone or carried in your inventory.</li>
        <li><strong>Live Stolen Device Stakes:</strong> If someone takes your phone or confiscates your active SIM card, your line remains active on that hardware while they hold it. Whoever possesses the device receives incoming calls and text messages in real time, creating tense roleplay windows for both thieves and investigators.</li>
        <li><strong>Carrier Recovery & Line Protection:</strong> You are never permanently locked out of your identity or number. If your phone or SIM card is stolen, lost, or confiscated, head to an authorized mobile carrier (Revealed at a later date or the usual places like electronic stores) to purchase a replacement SIM card.</li>
        <li><strong>Instant Remote Deactivation:</strong> The moment your new replacement SIM activates at the carrier counter, the old SIM card is instantly burned and permanently deactivated on the network. The stolen device drops to "No Service" or "SOS Only", immediately cutting off the thief and restoring your number safely to your new phone.</li>
      </ul>

      <h3>🐦 Bleeter Rework</h3>
      <p>
        Bleeter now includes threaded discussions, comment replies, live like counts, streamer mode, and cross-client delete sync.
      </p>
      <p style="padding: 12px 16px; background: rgba(0, 196, 204, 0.08); border-left: 3px solid #00c4cc; border-radius: 4px; font-size: 0.88rem; line-height: 1.5; color: #b0c4de; margin: 16px 0 24px 0;">
        <strong>Developer Note:</strong> All message threads, contact names, and Bleeter posts shown in previews are staging test data and do not reflect active player records or server lore.
      </p>

      <div style="margin: 32px auto; max-width: 780px; display: flex; flex-wrap: wrap; gap: 24px; justify-content: center; align-items: stretch;">
        <div style="flex: 1 1 320px; max-width: 360px; background: linear-gradient(180deg, rgba(16, 22, 32, 0.85) 0%, rgba(10, 14, 20, 0.95) 100%); border: 1px solid rgba(0, 196, 204, 0.28); border-radius: 18px; padding: 16px 14px 18px 14px; display: flex; flex-direction: column; align-items: center; justify-content: space-between; box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.05);">
          <div style="width: 100%; display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; padding: 0 4px;">
            <span style="font-size: 0.8rem; font-weight: 700; color: #00c4cc; letter-spacing: 0.6px; text-transform: uppercase;">
              <i class="fas fa-comment-dots" style="margin-right: 6px;"></i>Messages App
            </span>
            <span style="font-size: 0.7rem; padding: 2px 8px; border-radius: 10px; background: rgba(0, 196, 204, 0.15); color: #00c4cc; font-weight: 600; letter-spacing: 0.4px;">
              Live Demo
            </span>
          </div>
          <div style="position: relative; width: 100%; max-width: 320px; aspect-ratio: 285 / 608; border-radius: 20px; overflow: hidden; background: #000; box-shadow: 0 8px 24px rgba(0, 0, 0, 0.6); border: 1px solid rgba(255, 255, 255, 0.08); display: flex; align-items: center; justify-content: center;">
            <video controls playsinline loop muted autoplay style="width: 100%; height: 100%; object-fit: cover; object-position: center; display: block;">
              <source src="https://media.productionrp.org/pulse/phone_messages_teaser.mp4" type="video/mp4">
              Your browser does not support the video tag.
            </video>
          </div>
          <div style="margin-top: 14px; text-align: center; width: 100%; padding: 0 4px; flex-grow: 1; display: flex; flex-direction: column; justify-content: center;">
            <strong style="color: #fff; font-size: 0.92rem; display: block; margin-bottom: 4px;">Group Chats & Live Tapbacks</strong>
            <span style="font-size: 0.84rem; color: #9aa4b2; line-height: 1.45; display: block;">
              In-thread group messaging, custom wallpapers, search filters, and real-time emoji reactions.
            </span>
          </div>
        </div>

        <div style="flex: 1 1 320px; max-width: 360px; background: linear-gradient(180deg, rgba(16, 22, 32, 0.85) 0%, rgba(10, 14, 20, 0.95) 100%); border: 1px solid rgba(0, 196, 204, 0.28); border-radius: 18px; padding: 16px 14px 18px 14px; display: flex; flex-direction: column; align-items: center; justify-content: space-between; box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.05);">
          <div style="width: 100%; display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; padding: 0 4px;">
            <span style="font-size: 0.8rem; font-weight: 700; color: #00c4cc; letter-spacing: 0.6px; text-transform: uppercase;">
              <i class="fab fa-twitter" style="margin-right: 6px;"></i>Bleeter App
            </span>
            <span style="font-size: 0.7rem; padding: 2px 8px; border-radius: 10px; background: rgba(0, 196, 204, 0.15); color: #00c4cc; font-weight: 600; letter-spacing: 0.4px;">
              Social Feed
            </span>
          </div>
          <div style="position: relative; width: 100%; max-width: 320px; aspect-ratio: 285 / 608; border-radius: 20px; overflow: hidden; background: #000; box-shadow: 0 8px 24px rgba(0, 0, 0, 0.6); border: 1px solid rgba(255, 255, 255, 0.08); display: flex; align-items: center; justify-content: center;">
            <img src="https://media.productionrp.org/pulse/bleeter_teaser.gif" alt="Bleeter Threaded Discussions" style="width: 100%; height: 100%; object-fit: cover; object-position: center; display: block;" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
            <div style="display: none; width: 100%; height: 100%; align-items: center; justify-content: center; flex-direction: column; padding: 20px; color: #888; background: rgba(10, 14, 20, 0.9);">
              <i class="fab fa-twitter" style="font-size: 2.4rem; color: #00c4cc; margin-bottom: 12px;"></i>
              <span style="font-size: 0.9rem; font-style: italic;">[Bleeter Showcase Preview]</span>
              <span style="font-size: 0.8rem; color: #666; margin-top: 6px; text-align: center;">Threaded replies, like counters, and streamer mode</span>
            </div>
          </div>
          <div style="margin-top: 14px; text-align: center; width: 100%; padding: 0 4px; flex-grow: 1; display: flex; flex-direction: column; justify-content: center;">
            <strong style="color: #fff; font-size: 0.92rem; display: block; margin-bottom: 4px;">Threaded Discussions & Social</strong>
            <span style="font-size: 0.84rem; color: #9aa4b2; line-height: 1.45; display: block;">
              Nested comment replies, live like counters, streamer mode, and cross-client delete sync.
            </span>
          </div>
        </div>
      </div>

      <div style="margin: 36px 0 10px 0;">
        <span style="display: inline-block; padding: 3px 10px; border-radius: 4px; font-size: 0.75rem; font-weight: 700; text-transform: uppercase; background: rgba(0, 196, 204, 0.15); color: #00c4cc; letter-spacing: 0.8px;">Civilian Life & Cooperation</span>
      </div>
      <h2>👥 Civilian Squads & The LFG Hub</h2>
      <p>
        The new Groups App on your phone introduces a dedicated squad system to make group activities intuitive, coordinated, and rewarding. Whether forming an open crew on the public LFG board or setting up private squads with friends, the system ensures seamless coordination and fair compensation across the county.
      </p>

      <div style="margin: 30px auto; max-width: 840px; display: flex; flex-wrap: wrap; gap: 28px; justify-content: center; align-items: center;">

        <div style="flex: 1 1 300px; max-width: 350px; background: linear-gradient(180deg, rgba(16, 22, 32, 0.85) 0%, rgba(10, 14, 20, 0.95) 100%); border: 1px solid rgba(0, 196, 204, 0.28); border-radius: 18px; padding: 16px 14px 18px 14px; box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.05); text-align: center;">
          <div style="width: 100%; display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; padding: 0 4px;">
            <span style="font-size: 0.8rem; font-weight: 700; color: #00c4cc; letter-spacing: 0.6px; text-transform: uppercase;">
              <i class="fas fa-users" style="margin-right: 6px;"></i>Groups App
            </span>
            <span style="font-size: 0.7rem; padding: 2px 8px; border-radius: 10px; background: rgba(0, 196, 204, 0.15); color: #00c4cc; font-weight: 600; letter-spacing: 0.4px;">
              LFG Hub
            </span>
          </div>
          <div style="position: relative; width: 100%; max-width: 320px; aspect-ratio: 285 / 608; border-radius: 20px; overflow: hidden; background: #000; box-shadow: 0 8px 24px rgba(0, 0, 0, 0.6); border: 1px solid rgba(255, 255, 255, 0.08); margin: 0 auto;">
            <img src="https://media.productionrp.org/pulse/groups_teaser.png" alt="Civilian Squads & LFG Board" style="width: 100%; height: 100%; object-fit: cover; object-position: center; display: block;" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
            <div style="display: none; width: 100%; height: 100%; align-items: center; justify-content: center; flex-direction: column; padding: 20px; color: #888; background: rgba(10, 14, 20, 0.9);">
              <i class="fas fa-users" style="font-size: 2.4rem; color: #00c4cc; margin-bottom: 12px;"></i>
              <span style="font-size: 0.9rem; font-style: italic;">[Civilian Groups App Showcase]</span>
            </div>
          </div>
          <div style="margin-top: 14px; text-align: center; width: 100%; padding: 0 4px;">
            <strong style="color: #fff; font-size: 0.92rem; display: block; margin-bottom: 2px;">Civilian Squad Coordination</strong>
            <span style="font-size: 0.82rem; color: #9aa4b2;">Open crew listings, optional PIN passcodes, and squad coordination.</span>
          </div>
        </div>

        <div style="flex: 1 1 360px; max-width: 440px; display: flex; flex-direction: column; gap: 14px;">
          
          <div style="background: rgba(16, 22, 32, 0.6); border: 1px solid rgba(0, 196, 204, 0.18); border-radius: 12px; padding: 14px 16px;">
            <div style="display: flex; align-items: center; margin-bottom: 6px;">
              <i class="fas fa-bullhorn" style="color: #00c4cc; margin-right: 10px; font-size: 1rem;"></i>
              <strong style="color: #fff; font-size: 0.95rem;">Public LFG Board & Crew Recruitment</strong>
            </div>
            <p style="margin: 0; font-size: 0.85rem; color: #a0aec0; line-height: 1.5;">
              Solo citizens can browse public group listings or post open crews for activities like GoPostal, fishing, farming, or sanitation. Set an optional passcode for private squads.
            </p>
          </div>

          <div style="background: rgba(16, 22, 32, 0.6); border: 1px solid rgba(0, 196, 204, 0.18); border-radius: 12px; padding: 14px 16px;">
            <div style="display: flex; align-items: center; margin-bottom: 6px;">
              <i class="fas fa-id-badge" style="color: #00c4cc; margin-right: 10px; font-size: 1rem;"></i>
              <strong style="color: #fff; font-size: 0.95rem;">Unique Handles & Squad Identity</strong>
            </div>
            <p style="margin: 0; font-size: 0.85rem; color: #a0aec0; line-height: 1.5;">
              Form your squad and claim a unique civilian handle (e.g. <code>@handle</code>) with a cooldown to protect your identity across jobs and community boards.
            </p>
          </div>

          <div style="background: rgba(16, 22, 32, 0.6); border: 1px solid rgba(0, 196, 204, 0.18); border-radius: 12px; padding: 14px 16px;">
            <div style="display: flex; align-items: center; margin-bottom: 6px;">
              <i class="fas fa-map-marked-alt" style="color: #00c4cc; margin-right: 10px; font-size: 1rem;"></i>
              <strong style="color: #fff; font-size: 0.95rem;">Synced Objectives & Shared GPS</strong>
            </div>
            <p style="margin: 0; font-size: 0.85rem; color: #a0aec0; line-height: 1.5;">
              When the leader starts an activity, the entire squad receives matched HUD task trackers, stage updates, and synchronized GPS routes in real time.
            </p>
          </div>

          <div style="background: rgba(16, 22, 32, 0.6); border: 1px solid rgba(0, 196, 204, 0.18); border-radius: 12px; padding: 14px 16px;">
            <div style="display: flex; align-items: center; margin-bottom: 6px;">
              <i class="fas fa-coins" style="color: #00c4cc; margin-right: 10px; font-size: 1rem;"></i>
              <strong style="color: #fff; font-size: 0.95rem;">Automatic Balanced Payouts</strong>
            </div>
            <p style="margin: 0; font-size: 0.85rem; color: #a0aec0; line-height: 1.5;">
              Civilian jobs automatically split earnings and bonuses among active on-site members. Integrated across BigG Goods, Cluckin' Bell, Farms, LST Bus, NOMS, PostOp, Rogers Salvage, Taxi, Towing, Sanitation, and vehicle dismantling.
            </p>
          </div>

        </div>

      </div>

      <div style="margin: 36px 0 10px 0;">
        <span style="display: inline-block; padding: 3px 10px; border-radius: 4px; font-size: 0.75rem; font-weight: 700; text-transform: uppercase; background: rgba(220, 53, 69, 0.2); color: #ff6b6b; letter-spacing: 0.8px;">The Underworld</span>
      </div>
      <h2>👁️ Criminal Ecosystem: The Underground</h2>
      <p>
        The criminal ecosystem has been redesigned from the ground up to move away from generic public menus and magic map markers. Illicit operations are now grounded directly in the world, emphasizing preparation, squad coordination, and authentic risk.
      </p>
      <p style="padding: 14px 18px; background: rgba(220, 53, 69, 0.08); border-left: 4px solid #ff6b6b; border-radius: 4px; font-size: 0.95rem; line-height: 1.6;">
        <strong>Developer Note:</strong> We are deliberately keeping mechanics, progression paths, and tools confidential. We will share detailed guides and information on release, but how operations unfold and where opportunities lie are meant to be experienced in the city.
      </p>

      <h3>⚖️ Unified Operations & Scene Stability</h3>
      <p>
        Under the hood, criminal activities now run on a central operations engine designed for scene integrity and reliability:
      </p>
      <ul>
        <li><strong>Scene Management:</strong> Active operations are coordinated to prevent overlapping scenes and conflicting timers.</li>
        <li><strong>Crew Synchronization:</strong> Jobs synchronize stages, timers, and objectives across all participating members of the crew.</li>
        <li><strong>Disconnect Protection:</strong> If a crew member experiences a client crash during an active job, grace windows protect the operation so scene progress is not instantly lost.</li>
      </ul>

      <div style="margin: 36px 0 10px 0;">
        <span style="display: inline-block; padding: 3px 10px; border-radius: 4px; font-size: 0.75rem; font-weight: 700; text-transform: uppercase; background: rgba(0, 196, 204, 0.15); color: #00c4cc; letter-spacing: 0.8px;">Public Safety & Forensics</span>
      </div>
      <h2>🔬 Forensics & Crime Lab Investigation</h2>
      <p>
        As criminal methods get smarter, detectives and forensic specialists have new investigative tools to build court cases.
      </p>

      <h3>💻 Cyber Forensics & Seized Devices</h3>
      <p>
        When suspects are arrested, securing their phone preserves the active subscriber line inside it:
      </p>
      <ul>
        <li><strong>Hardware Triage:</strong> Detectives can run diagnostic scans on seized devices at the station. Scans verify firmware authenticity, extract serials and IMEI tags, pull the active number, and document device details for evidence filings.</li>
        <li><strong>Live Interception:</strong> If police secure a phone before it is wiped or deactivated, officers can watch incoming calls and texts from associates in real time.</li>
      </ul>

      <h3>🔬 Evidence Processing, Ballistics & Crime Lab Reports</h3>
      <p>
        Investigating crime scenes and processing physical evidence now forms a complete forensic loop from the field to the crime lab:
      </p>
      <ul>
        <li><strong>Ballistic Matching:</strong> Spent bullet casings left at scenes match the unique ballistic signature and serial number of the firearm that fired them.</li>
        <li><strong>Spectral Analysis Queues:</strong> Collected casings, ballistics, and biological samples enter real-time lab queues with live progress timers, location logs, and temperature readings.</li>
        <li><strong>Tamper-Evident Evidence Seals:</strong> Once processed through the Crime Lab, devices and physical evidence receive serialized barcode stickers in inventory. Tampering with or scraping official tags triggers legal alerts, safeguarding chain of custody for court.</li>
        <li><strong>Certified Crime Lab Reports:</strong> Crime lab staff can print official, court-admissible forensic certificates for firearms, spent casings, DNA swabs, lifted fingerprints, and scratched VIN fragments.</li>
      </ul>

      <div class="pulse-gallery forensics-gallery" data-interval="4500">
        <input type="radio" name="forensics-tab" id="ftab-queue" class="pulse-tab-input forensics-tab-input" checked>
        <input type="radio" name="forensics-tab" id="ftab-seal" class="pulse-tab-input forensics-tab-input">
        <input type="radio" name="forensics-tab" id="ftab-cert" class="pulse-tab-input forensics-tab-input">

        <div class="pulse-gallery-nav forensics-nav">
          <label for="ftab-queue" class="pulse-gallery-pill f-pill f-pill-queue active">Live Analysis Queue</label>
          <label for="ftab-seal" class="pulse-gallery-pill f-pill f-pill-seal">Evidence Seal</label>
          <label for="ftab-cert" class="pulse-gallery-pill f-pill f-pill-cert">Crime Lab Report</label>
        </div>

        <div class="pulse-gallery-panels forensics-panels">

          <div class="pulse-gallery-panel f-panel f-panel-queue active">
            <div class="pulse-gallery-box forensics-img-box" title="Click to view next stage">
              <img src="https://media.productionrp.org/pulse/forensics_on_casing.png" alt="Evidence Analysis Queue" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
              <div style="display: none; height: 320px; width: 100%; align-items: center; justify-content: center; flex-direction: column; padding: 20px; color: #888;">
                <i class="fas fa-search" style="font-size: 2.4rem; color: #00c4cc; margin-bottom: 12px;"></i>
                <span style="font-size: 0.9rem; font-style: italic;">[Evidence Queue Preview]</span>
              </div>
            </div>
            <div class="pulse-gallery-caption forensics-caption">
              <strong>Live Analysis Queue & Telemetry</strong>
              <p>Collected casings and ballistics enter real-time spectral scanning queues with live progress bars, location logging, and temperature tags.</p>
            </div>
          </div>

          <div class="pulse-gallery-panel f-panel f-panel-seal">
            <div class="pulse-gallery-box forensics-img-box" title="Click to view next stage">
              <img src="https://media.productionrp.org/pulse/evidence_tag.png" alt="Crime Lab Evidence Tagged Phone" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
              <div style="display: none; height: 320px; width: 100%; align-items: center; justify-content: center; flex-direction: column; padding: 20px; color: #888;">
                <i class="fas fa-tag" style="font-size: 2.4rem; color: #00c4cc; margin-bottom: 12px;"></i>
                <span style="font-size: 0.9rem; font-style: italic;">[Evidence Seal Preview]</span>
              </div>
            </div>
            <div class="pulse-gallery-caption forensics-caption">
              <strong>Tamper-Evident Evidence Seals</strong>
              <p>Seized phones and items receive official Crime Lab barcodes and serial tags in inventory, establishing chain of custody.</p>
            </div>
          </div>

          <div class="pulse-gallery-panel f-panel f-panel-cert">
            <div class="pulse-gallery-box forensics-img-box" title="Click to view next stage">
              <img src="https://media.productionrp.org/pulse/lab_results.png" alt="Official Crime Lab Certificate" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
              <div style="display: none; height: 320px; width: 100%; align-items: center; justify-content: center; flex-direction: column; padding: 20px; color: #888;">
                <i class="fas fa-certificate" style="font-size: 2.4rem; color: #00c4cc; margin-bottom: 12px;"></i>
                <span style="font-size: 0.9rem; font-style: italic;">[Official Crime Lab Report Preview]</span>
              </div>
            </div>
            <div class="pulse-gallery-caption forensics-caption">
              <strong>Certified Forensic Documentation</strong>
              <p>Official, court-admissible forensic certificates stamped with case identifiers, matching casing links, and technician certification.</p>
            </div>
          </div>

        </div>
      </div>

      <div style="margin: 36px 0 10px 0;">
        <span style="display: inline-block; padding: 3px 10px; border-radius: 4px; font-size: 0.75rem; font-weight: 700; text-transform: uppercase; background: rgba(0, 196, 204, 0.15); color: #00c4cc; letter-spacing: 0.8px;">Civil Infrastructure & Justice</span>
      </div>
      <h2>⚖️ Citations & Debt Escalations</h2>
      <p>
        State debt is no longer a single, faceless bucket of money on your banking screen where you are forced to pay everything or nothing. It is a fully itemized legal ledger that gives you real player agency: you can choose which bills to pay, which ones to ignore, and which bogus citations you want to fight in court.
      </p>
      <p style="padding: 12px 16px; background: rgba(0, 196, 204, 0.08); border-left: 3px solid #00c4cc; border-radius: 4px; font-size: 0.88rem; line-height: 1.5; color: #b0c4de; margin: 16px 0 24px 0;">
        <strong>Developer Note:</strong> All fine amounts, back tax balances, and welfare figures shown in screenshots are staging test data used for balance testing and do not represent final economy tuning.
      </p>

      <div style="margin: 30px auto; max-width: 840px; display: flex; flex-wrap: wrap; gap: 28px; justify-content: center; align-items: center;">

        <div style="flex: 1 1 300px; max-width: 350px; background: linear-gradient(180deg, rgba(16, 22, 32, 0.85) 0%, rgba(10, 14, 20, 0.95) 100%); border: 1px solid rgba(0, 196, 204, 0.28); border-radius: 18px; padding: 16px 14px 18px 14px; box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.05); text-align: center;">
          <div style="width: 100%; display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; padding: 0 4px;">
            <span style="font-size: 0.8rem; font-weight: 700; color: #00c4cc; letter-spacing: 0.6px; text-transform: uppercase;">
              <i class="fas fa-balance-scale" style="margin-right: 6px;"></i>State Justice App
            </span>
            <span style="font-size: 0.7rem; padding: 2px 8px; border-radius: 10px; background: rgba(0, 196, 204, 0.15); color: #00c4cc; font-weight: 600; letter-spacing: 0.4px;">
              Civil Ledger
            </span>
          </div>
          <div style="position: relative; width: 100%; max-width: 320px; aspect-ratio: 290 / 622; border-radius: 20px; overflow: hidden; background: #000; box-shadow: 0 8px 24px rgba(0, 0, 0, 0.6); border: 1px solid rgba(255, 255, 255, 0.08); margin: 0 auto;">
            <img src="https://media.productionrp.org/pulse/fines.png" alt="Itemized Debt and Citations Ledger" style="width: 100%; height: 100%; object-fit: cover; object-position: center; display: block;" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
            <div style="display: none; width: 100%; height: 100%; align-items: center; justify-content: center; flex-direction: column; padding: 20px; color: #888; background: rgba(10, 14, 20, 0.9);">
              <i class="fas fa-file-invoice-dollar" style="font-size: 2.4rem; color: #00c4cc; margin-bottom: 12px;"></i>
              <span style="font-size: 0.9rem; font-style: italic;">[State Justice Civil Ledger Preview]</span>
            </div>
          </div>
          <div style="margin-top: 14px; text-align: center; width: 100%; padding: 0 4px;">
            <strong style="color: #fff; font-size: 0.92rem; display: block; margin-bottom: 2px;">Itemized Debt & Choice</strong>
            <span style="font-size: 0.82rem; color: #9aa4b2;">Pay selective line items, contest bogus citations in court, or settle your balance in full.</span>
          </div>
        </div>

        <div style="flex: 1 1 360px; max-width: 440px; display: flex; flex-direction: column; gap: 14px;">
          
          <div style="background: rgba(16, 22, 32, 0.6); border: 1px solid rgba(0, 196, 204, 0.18); border-radius: 12px; padding: 14px 16px;">
            <div style="display: flex; align-items: center; margin-bottom: 6px;">
              <i class="fas fa-hand-holding-usd" style="color: #00c4cc; margin-right: 10px; font-size: 1rem;"></i>
              <strong style="color: #fff; font-size: 0.95rem;">Pay What You Want & Contest The Rest</strong>
            </div>
            <p style="margin: 0; font-size: 0.85rem; color: #a0aec0; line-height: 1.5;">
              Because citations and back taxes are itemized, you call the shots. Pay your property taxes to protect your home while refusing a bogus citation to fight in court. Or say to hell with the system entirely and live on the run.
            </p>
          </div>

          <div style="background: rgba(16, 22, 32, 0.6); border: 1px solid rgba(0, 196, 204, 0.18); border-radius: 12px; padding: 14px 16px;">
            <div style="display: flex; align-items: center; margin-bottom: 6px;">
              <i class="fas fa-layer-group" style="color: #00c4cc; margin-right: 10px; font-size: 1rem;"></i>
              <strong style="color: #fff; font-size: 0.95rem;">Proportional Stakes & Stacking Penalties</strong>
            </div>
            <p style="margin: 0; font-size: 0.85rem; color: #a0aec0; line-height: 1.5;">
              A routine speeding ticket will never get your door kicked in or your car seized. But stacking dozens of unpaid violations brings escalating heat: overdue notices, 60% welfare garnishments, and impound release holds.
            </p>
          </div>

          <div style="background: rgba(16, 22, 32, 0.6); border: 1px solid rgba(0, 196, 204, 0.18); border-radius: 12px; padding: 14px 16px;">
            <div style="display: flex; align-items: center; margin-bottom: 6px;">
              <i class="fas fa-gavel" style="color: #00c4cc; margin-right: 10px; font-size: 1rem;"></i>
              <strong style="color: #fff; font-size: 0.95rem;">Obstruction of State Asset Recovery</strong>
            </div>
            <p style="margin: 0; font-size: 0.85rem; color: #a0aec0; line-height: 1.5;">
              Unpaid judgments eventually bring lawful asset seizures and court summons. Evading hearings, hiding assets, or resisting seizures triggers felony charges for <em>Obstruction of State Asset Recovery</em>, resulting in arrest warrants, asset forfeiture, and license suspensions.
            </p>
          </div>

          <div style="background: rgba(16, 22, 32, 0.6); border: 1px solid rgba(0, 196, 204, 0.18); border-radius: 12px; padding: 14px 16px;">
            <div style="display: flex; align-items: center; margin-bottom: 6px;">
              <i class="fas fa-check-circle" style="color: #00c4cc; margin-right: 10px; font-size: 1rem;"></i>
              <strong style="color: #fff; font-size: 0.95rem;">Full-Circle Resolution & Relief</strong>
            </div>
            <p style="margin: 0; font-size: 0.85rem; color: #a0aec0; line-height: 1.5;">
              Multiple avenues exist to settle your record: pay individual citations on your phone or at the courthouse, clear your balance at once, work off oldest debts through prison labor wages or even speak to a magistrate at the DOJ for assistance.
            </p>
          </div>

        </div>

      </div>

      <div style="margin: 36px 0 10px 0;">
        <span style="display: inline-block; padding: 3px 10px; border-radius: 4px; font-size: 0.75rem; font-weight: 700; text-transform: uppercase; background: rgba(0, 196, 204, 0.15); color: #00c4cc; letter-spacing: 0.8px;">Looking Forward</span>
      </div>
      <h2>🚀 The Road Ahead</h2>
      <p>
        Connecting all these systems so they talk to each other has been a massive undertaking. Seeing evidence from a scene lead to a confiscated phone, which leads to a court subpoena, while civilian squads work synced jobs across the county is what makes the city feel alive.
      </p>
      <p>
        We are continuing to polish, balance, and test every day. Thank you to our volunteer dev team, our testers, and everyone who keeps flying in and keeping the community active.
      </p>

      <div style="margin: 28px 0; padding: 18px 22px; background: linear-gradient(135deg, rgba(255, 140, 0, 0.08) 0%, rgba(16, 22, 32, 0.85) 100%); border: 1px solid rgba(255, 159, 67, 0.28); border-left: 4px solid #ff9f43; border-radius: 12px; box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);">
        <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 6px;">
          <span style="font-size: 1.3rem;">🎃</span>
          <strong style="color: #ff9f43; font-size: 1rem; letter-spacing: 0.4px;">Happy Halloween & Spooky Season from Production RP!</strong>
        </div>
        <p style="margin: 0; font-size: 0.88rem; color: #cbd5e1; line-height: 1.55;">
          Whether you are out trick-or-treating around the county or flying in for daily roleplay, thank you for spending another great autumn with us. Have a fun and safe Halloween from the entire team, enjoy the season, and get your squads ready! 🍂
        </p>
      </div>

      <p style="font-weight: 600; color: #fff;">
        Stay tuned, keep roleplaying, and happy haunting!
      </p>
    `
  },
  {
    id: '2026-07-09',
    monthLabel: 'Summer 2026',
    content: `
      <div class="pulse-hero">
        <h1>Summer Pulse: The Next Era Taking Shape</h1>
      </div>

      <p>
        It has been a minute since our last Pulse, and we know that gap has been felt. We had intended to release this update sooner, but real-life interruptions delayed getting it finalized and posted. The reality of long-haul development is that building robust, future-proof frameworks takes a massive amount of time, and the quiet on the front end often masks the absolute grind happening behind the scenes.
      </p>
      <p>
        Development is in full swing. Countless hours are being poured in, with developers volunteering their free time, to clear out years of old code and build something better. We are now in a strong polishing phase for a lot of this foundational work, and we want to give you a direct look at the massive systems being brought to life.
      </p>

      <h2>🚜 Local Jobs</h2>
      <p>
        We finally busted into a stronger phase of job gameplay and minigame integration. Historically, local jobs have been pretty boring. You hit E at a spot, watch an emote, and go collect your item. 
      </p>
      <p>
        We are actively establishing a "new normal" for how local jobs function, focusing on introducing immersive ways for you to actually do the work. One of the clearest examples of this new direction is the Los Santos Department of Water & Power (LSDWP) job flow. This teaser shows the kind of interaction standards and minigame experience we are now aiming to carry across the server:
      </p>
      <div style="margin: 20px auto; max-width: 800px;">
        <video controls style="width: 100%; border-radius: 8px;">
          <source src="https://media.productionrp.org/pulse/lsdwp-job-teaser.mp4" type="video/mp4">
          Your browser does not support the video tag.
        </video>
      </div>

      <h2>🛒 Store QOL & Furniture Expansion</h2>
      <p>
        Adding items to the world is easy. Building a scalable, immersive system to manage and purchase them is hard. We have aimed to keep the user experience consistent, clear, and easy to navigate. 
      </p>
      <div style="margin: 20px auto; max-width: 800px;">
        <video controls style="width: 100%; border-radius: 8px;">
          <source src="https://media.productionrp.org/pulse/StoreQOL.mp4" type="video/mp4">
          Your browser does not support the video tag.
        </video>
      </div>
      <ul>
        <li><strong>Smart Categorization:</strong> Props and items now feature detailed descriptions and are sorted logically into specific stores and categories.</li>
        <li><strong>Holiday Restrictions:</strong> We built a new holiday restriction system under the hood. Things like Halloween and Christmas props can now be configured to <em>only</em> be placed during their respective holiday windows, in turn making things available to the public faster and requiring less development intervention during those holidays!</li>
        <li><strong>Custom Interiors:</strong> We now have the ability to customize the location of specific furniture store interiors directly at the store definition level, creating more immersion for each shop.</li>
      </ul>

      <h2>🔨 Crafting, Tablets, and MDT Rewrites</h2>
      <p>
        We are officially wrapping up the new Crafting framework. Rather than just adding a few recipes, we have built a robust system that can be infinitely built on top of in the future, allowing for deep progression and discovery.
      </p>
      <p>
        Alongside crafting, the Tablet system is currently getting updated for maintainability and to house several more fun and immersive features, including porting Racing directly into the tablet. We are also starting work on an entirely new MDT right now. We have reviewed extensive player feedback, and it has been constructed and laid out to spec out a much faster, reliable system that integrates deeply with the new framework while filling the gaps the previous just could not fill entirely.
      </p>

      <h2>👕 Laying Groundwork: The Future of Clothing</h2>
      <p>
        The way clothing worked in the past was rigid and hardcoded, making it difficult to expand upon. Recently, we completely rebuilt how clothing functions under the hood. Instead of outfits just being saved data, we are turning them into actual structures that the server understands.
      </p>
      <p>
        Why did we do this? To pave the way for <strong>Clothing Bags</strong> in the future. By modernizing how clothing is handled, we laid the exact groundwork needed for you to eventually carry specific outfits as actual items in your inventory and change clothes dynamically on the fly. As a proof of concept, we've already migrated the Prison Outfits over to this new system!
      </p>
      <div style="margin: 20px auto; max-width: 800px; text-align: center;">
        <img src="https://media.productionrp.org/pulse/prison_outfits.png" alt="Prison Outfits Inventory Teaser" style="max-width: 100%; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.3);">
      </div>

      <h2>🎨 Accessibility and Minigame Consistency</h2>
      <p>
        A major focus has been visual consistency and accessibility across new gameplay. We are standardizing how new minigames look and behave so interactions feel familiar and readable.
      </p>
      <p>
        That includes adding and testing colorblind-friendly alternatives where possible. Some legacy or ultra-simple interactions may still be difficult depending on the mechanic, but our goal is to provide alternatives without creating a "free bypass" meta where accessibility options become a competitive exploit.
      </p>
      <h2>🧬 Seamless Character Import</h2>
      <p>
        One massive quality-of-life feature we are incredibly excited about is the ability to import your characters directly from the 1.0 server into 2.0! As long as your character isn't dead and hasn't already been copied over, you can pull their exact appearance directly into the new creator. 
      </p>
      <p>
        From there, you get to edit them however you want before saving. No more trying to memorize every single slider value, and no more holding up a screenshot to your monitor trying to painstakingly recreate them.
      </p>
      <div style="margin: 20px auto; max-width: 800px; text-align: center;">
        <img src="https://media.productionrp.org/pulse/character_import.png" alt="Character Import Feature" style="max-width: 100%; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.3);">
      </div>

      <h2>🎲 Casino Development</h2>
      <p>
        The Casino game floor is pretty much finished! The games, chips, and core mechanics like Slots, Blackjack, and the Inside Track are all fully accessible, and we have heavily enhanced the UI controls and notifications. While backend features like memberships will come later, it is incredibly important to us that the game floor is fully open and ready for you on release.
      </p>

      <h2>🛠️ Behind the Scenes: UI Studio</h2>
      <p>
        Building a city of this scale means we need to work smarter, not just harder. We recently built a standalone "UI Studio" that allows our team to design, test, and iterate on menus and interfaces in an isolated environment. 
      </p>
      <p>
        This means our developers can rapid-fire test new layouts, animations, and button logic instantly while they are coding, before they even bring it into the live city environment for final integration. By investing time into this internal tool now, we have drastically sped up our UI development pipeline for all future features.
      </p>

      <h3>The Road Ahead</h3>
      <p>
        When you read a pulse and only see a few bullet points, it can feel like nothing is happening. We want to assure you that the lack of public updates is simply because we are heads-down, grinding through the incredibly complex foundation needed to make 2.0 a reality. 
      </p>
      <p>
        Before we sign off, we want to give a massive shout-out to the players who continue to fly into the 1.0 server and keep the Discord active every single day. Seeing that constant life in the community is what fuels the team to keep pushing through this final stretch.
      </p>
      <p>
        Thank you for your patience, for trusting the process, and for staying locked in with us.
      </p>
    `
  },
  {
    id: '2026-02-03',
    monthLabel: 'February / March 2026',
    content: `
      <div class="pulse-hero">
        <h1>Pressure, Progress, and Pushing Forward</h1>
      </div>

      <p>
        February and March have both brought strong momentum for the city. This part of the year is where great ideas start turning into long-term progress, and we're seeing that happen across PRP. Storylines are maturing, departments are recalibrating, crews are sharpening strategy, and businesses are finding their footing for the season ahead.
      </p>
      <p>
        This stretch of the year is about execution. Not hype. Not noise. Execution.
      </p>

      <h2>🧰 Dev Things</h2>
      <p>
        Development this cycle has been all about tackling the biggest systems head-on, especially inventory. We're looping back through every old inventory function and feature to make sure it's either ported over, improved, or replaced. This isn't just a visual update. It's a total rebuild. Everything we've used since Alpha/Beta for inventory is gone. The new system is built from the ground up, not just in how it looks, but how it works under the hood.
      </p>
      <p>
        For years, there were pain points and limitations that held us back or forced us to work around them: item uniqueness, stacking issues, expiry issues, and for example a move function that was hundreds of if/else statements. Trying to fix bugs or add features often broke core parts of the inventory, and it became unmanageable no matter how much we optimized. We did great things with it, but we hit the ceiling of what was possible. That's why we needed to start fresh, using the new skillsets we've gained over the years. The new inventory is designed to be future-proof, giving us capabilities and flexibility we never had before. This means more features, smoother interactions, and a foundation that lets us keep building without running into the same old walls.
      </p>
      <p>
        Systems like the casino are already hooked up to the new inventory, and we're making sure every other major system, jobs, crafting, property, and more, gets the same treatment. We're also working on things like the MDT and other core systems, all intended to launch with the update. If inventory isn't finished in time to let us swap over to those other systems, we'll focus on getting inventory right first and prioritize what’s ready for launch, with more to follow in updates. Anything that doesn't make it for launch will be updated or migrated in after, so nothing gets left behind.
      </p>
      <p>
        Huge thanks to the dev team for the steady output, problem solving, and keeping the server stable while we push these big changes. Sustainable growth takes patience and technical precision. That work is what sets us up for the future.
      </p>

      <h3>📦 Refresh Status and Launch Plan</h3>
      <p>
        The wipe date question comes up often, so we want to address it clearly: we do not have a fixed wipe deadline to announce right now, but our target is within the next 3 months.
      </p>
      <p>
        Every member of staff is volunteering their time, and PRP is not a plug-and-play server. Most of what you see in the city is custom built for this environment, which means systems must be designed, coded, tested, adjusted, retested, and hardened before they are ready.
      </p>
      <p>
        We are not interested in wiping just to say a wipe happened. A wipe needs to ship with a stable foundation that genuinely improves the experience, not just a quick visual reset.
      </p>
      <p>
        Our focus is steady, quality progress. We are finalizing the systems that need real balancing, fixes, and stability first, then continuing with larger overhauls after launch.
      </p>
      <p>
        To be clear, we are not holding this up for another giant system overhaul first. Features like a full phone overhaul can come after the foundation is live. Right now, priority is finishing what directly impacts the wipe experience and getting it right.
      </p>
      <h3>🎯 The Plan</h3>
      <p>
        Here's the current focus:
      </p>
      <ul>
        <li><strong>Finish inventory</strong> - The core system is in place, and we're closing out the final pieces.</li>
        <li><strong>Finalize reset economy pricing</strong> - Pricing for the economy reset is being wrapped up now.</li>
        <li><strong>Ship when core systems are stable</strong> - Then continue improving through regular follow-up updates.</li>
      </ul>
      <p>
        Inventory is very close to completion. We have also recently teased supporters with the casino progress, and that system is now properly working and integrated with the new inventory.
      </p>
      <div style="display: flex; flex-wrap: wrap; gap: 18px; justify-content: center; max-width: 900px; margin: 16px auto 0 auto;">
        <img src="https://media.productionrp.org/pulse/inside-track.png" alt="Inside Track Casino Screenshot" style="width: 420px; max-width: 100%; border-radius: 4px; object-fit: cover; cursor: pointer;" onclick="event.stopPropagation();const overlay=document.createElement('div');overlay.style.cssText='position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.8);z-index:9998;display:flex;align-items:center;justify-content:center;cursor:pointer';const close=document.createElement('div');close.innerHTML='✕';close.style.cssText='position:absolute;top:20px;right:30px;color:white;font-size:40px;font-weight:bold;cursor:pointer;z-index:10000';const img=document.createElement('img');img.src=this.src;img.style.cssText='max-width:90vw;max-height:90vh;border-radius:8px;box-shadow:0 4px 20px rgba(0,0,0,0.5)';overlay.appendChild(close);overlay.appendChild(img);document.body.appendChild(overlay);overlay.onclick=()=>overlay.remove();close.onclick=()=>overlay.remove();" />
        <img src="https://media.productionrp.org/pulse/slots.png" alt="Slots Casino Screenshot" style="width: 420px; max-width: 100%; border-radius: 4px; object-fit: cover; cursor: pointer;" onclick="event.stopPropagation();const overlay=document.createElement('div');overlay.style.cssText='position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.8);z-index:9998;display:flex;align-items:center;justify-content:center;cursor:pointer';const close=document.createElement('div');close.innerHTML='✕';close.style.cssText='position:absolute;top:20px;right:30px;color:white;font-size:40px;font-weight:bold;cursor:pointer;z-index:10000';const img=document.createElement('img');img.src=this.src;img.style.cssText='max-width:90vw;max-height:90vh;border-radius:8px;box-shadow:0 4px 20px rgba(0,0,0,0.5)';overlay.appendChild(close);overlay.appendChild(img);document.body.appendChild(overlay);overlay.onclick=()=>overlay.remove();close.onclick=()=>overlay.remove();" />
      </div>
      <p style="padding: 12px; background: rgba(79, 195, 247, 0.08); border-radius: 6px; font-size: 0.95em;">
        <strong>Release Standard:</strong> We will launch when the foundation is stable and ready, then keep improving through regular updates.
      </p>
      <h3>🔄 Post-Launch Focus</h3>
      <p>
        Once the refresh is live, development does not stop. The priority shifts to improving and expanding local jobs, refining new systems based on real player feedback, updating and rebuilding older systems that need attention, and continuing to roll out content that was not ready for day one.
      </p>
      <p>
        There are still specific inventory interactions and item behaviors being finalized as part of the porting process. That is normal for a system of this size, and we want those pieces stable before calling it done.
      </p>
      <h3>💬 Real Talk</h3>
      <p>
        We know people want a date, and we understand why. The balance we are aiming for is straightforward: do this the right way without dragging momentum.
      </p>

      <h3>🙋 Join Our Team!</h3>
      <p>
        Want to help make ProductionRP even better? We're looking for passionate community members to join our team and help shape the future of your home city. If you have skills or interest in any of these areas, we'd love to hear from you:
      </p>
      <ul>
        <li><strong>Clothing & Art</strong>: Texture work, clothing design, and creating inventory icons. If you enjoy creative work, your help can make a big difference.</li>
        <li><strong>Vehicle Handling</strong>: Tuning physics, handling lines, and making vehicles feel great in-game. If you love cars or tweaking how things drive, this is for you.</li>
        <li><strong>PD Fleet Art & Livery</strong>: Fitting artwork to police vehicles and helping finalize fleet quality. If you have an eye for detail, we need you.</li>
        <li><strong>Development</strong>: Coding, scripting, or systems work (FiveM, Lua, JS, or general dev). If you want to build something unique, there's plenty to do.</li>
      </ul>
      <p>
        You don't need to know everything. Just bring your passion and skills. We have tools and workflows to help you focus on what you do best.
      </p>
      <p>
        Interested? <a href="https://core.productionrp.org/developerapplication" target="_blank" rel="noopener noreferrer" style="color: #4fc3f7; text-decoration: none; font-weight: 600;">Apply here to join the team</a> and help make your home even better!
      </p>

      <h2>🏙️ Civ Things</h2>
      <p>
        Civilian RP continues to define the city's culture. Businesses that maintain consistent hours, create recurring events, and collaborate with others are setting the tone for longevity.
      </p>
      <p>
        We are seeing stronger use of properties, more cross-business partnerships, and better event planning. That is the direction we want. March is an ideal time to experiment with spring-themed events, business collaborations, or long-term character arcs tied to your establishment.
      </p>
      <p>
        If you hold a space in this city, use it well. Idle properties do not build RP engaging ones do.
      </p>

      <h3>Allocation Businesses</h3>
      <p>
        <a href="https://docs.google.com/forms/d/e/1FAIpQLSdApLNpWjvlnZsaX79W-D_VgzoY8iLQTnpjKxavErhbM5Qo9Q/viewform" target="_blank" rel="noopener noreferrer" style="color: #4fc3f7; text-decoration: none; font-weight: 600;">Click here to apply</a>.
      </p>
      <ul>
        <li><strong>Kings Manufacturing</strong></li>
      </ul>

      <h2>💀 Crew Things</h2>
      <p>
        Criminal RP has been evolving. The emphasis remains clear: calculated operations, structured leadership, and meaningful consequences. Random crime without narrative impact does not strengthen your crew's reputation. Strategic moves do.
      </p>
      <div style="margin: 18px 0; padding: 12px; background: rgba(79, 195, 247, 0.08); border-radius: 6px;">
        <h4 style="margin-top: 0;">Crew Recon Mission Highlight</h4>
        <div style="margin-bottom: 12px;">
          <p style="margin-bottom: 8px;">
            Not every task assigned to our official crews is about high-intensity conflict or headline-making crime, and that’s by design.
          </p>
          <p style="margin-bottom: 8px;">
            Take The Lost, for example. Their recent assignment focused on reconnaissance, laying the groundwork for future interactions rather than triggering immediate chaos. The result? A standout display of roleplay depth, creativity, and character-driven storytelling that really brought their crew identity to life.
          </p>
          <p style="margin-bottom: 8px;">
            As the Crew Team continues to grow and gather feedback, our goal is to craft tasks that align with each crew’s unique personality and strengths, giving everyone the chance to shine in different ways.
          </p>
          <p style="margin-bottom: 8px;">
            Be sure to check out some of the media captured by The Lost during this assignment, along with highlights of their interactions. It’s a great example of how immersive RP can be without ever firing a shot.
          </p>
          <div style="display: flex; align-items: center; gap: 8px; margin-top: 8px;">
            <span style="font-size: 1em; color: #aaa;">Merryweather has no idea what is to come.</span>
            <img src="https://media.productionrp.org/pulse/cipher.png" alt="Cipher" style="height: 28px; vertical-align: middle;" />
          </div>
        </div>
        <div style="display: flex; flex-direction: row; gap: 16px; justify-content: center; align-items: flex-end; flex-wrap: wrap; max-width: 900px; margin: 0 auto;">
          <img src="https://media.productionrp.org/pulse/recon-1.png" alt="Recon Screenshot 1" style="height: 220px; width: auto; max-width: 100%; border-radius: 4px; object-fit: cover; cursor: pointer;" onclick="event.stopPropagation();const overlay=document.createElement('div');overlay.style.cssText='position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.8);z-index:9998;display:flex;align-items:center;justify-content:center;cursor:pointer';const close=document.createElement('div');close.innerHTML='✕';close.style.cssText='position:absolute;top:20px;right:30px;color:white;font-size:40px;font-weight:bold;cursor:pointer;z-index:10000';const img=document.createElement('img');img.src=this.src;img.style.cssText='max-width:90vw;max-height:90vh;border-radius:8px;box-shadow:0 4px 20px rgba(0,0,0,0.5)';overlay.appendChild(close);overlay.appendChild(img);document.body.appendChild(overlay);overlay.onclick=()=>overlay.remove();close.onclick=()=>overlay.remove();" />
          <img src="https://media.productionrp.org/pulse/recon-2.png" alt="Recon Screenshot 2" style="height: 220px; width: auto; max-width: 100%; border-radius: 4px; object-fit: cover; cursor: pointer;" onclick="event.stopPropagation();const overlay=document.createElement('div');overlay.style.cssText='position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.8);z-index:9998;display:flex;align-items:center;justify-content:center;cursor:pointer';const close=document.createElement('div');close.innerHTML='✕';close.style.cssText='position:absolute;top:20px;right:30px;color:white;font-size:40px;font-weight:bold;cursor:pointer;z-index:10000';const img=document.createElement('img');img.src=this.src;img.style.cssText='max-width:90vw;max-height:90vh;border-radius:8px;box-shadow:0 4px 20px rgba(0,0,0,0.5)';overlay.appendChild(close);overlay.appendChild(img);document.body.appendChild(overlay);overlay.onclick=()=>overlay.remove();close.onclick=()=>overlay.remove();" />
          <img src="https://media.productionrp.org/pulse/recon-notes.png" alt="Recon Notes" style="width: 140px; height: 220px; max-width: 100%; border-radius: 4px; object-fit: contain; background: #222; cursor: pointer;" onclick="event.stopPropagation();const overlay=document.createElement('div');overlay.style.cssText='position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.8);z-index:9998;display:flex;align-items:center;justify-content:center;cursor:pointer';const close=document.createElement('div');close.innerHTML='✕';close.style.cssText='position:absolute;top:20px;right:30px;color:white;font-size:40px;font-weight:bold;cursor:pointer;z-index:10000';const img=document.createElement('img');img.src=this.src;img.style.cssText='max-width:90vw;max-height:90vh;border-radius:8px;box-shadow:0 4px 20px rgba(0,0,0,0.5)';overlay.appendChild(close);overlay.appendChild(img);document.body.appendChild(overlay);overlay.onclick=()=>overlay.remove();close.onclick=()=>overlay.remove();" />
        </div>
        <style>
        @media (max-width: 700px) {
          .pulse-crew-recon-gallery img {
            display: block !important;
            margin: 0 auto 10px auto !important;
            width: 90% !important;
            height: auto !important;
            max-width: 320px !important;
          }
        }
        </style>
        <p style="margin-top: 8px; font-size: 0.95em; color: #555;">Great work to everyone involved. This is the kind of creative effort that makes the city feel alive.</p>
      </div>
      <p>
        Official crews that have leaned into planning, communication, and layered execution are seeing stronger results. That effort sets the standard.
      </p>
      <p>
        As we move into April, expect continued focus on mentality shifts. Crime should feel coordinated, immersive, and consequential. Build tension. Build identity. Build presence.
      </p>
      <p>
        Continue supporting the Crew Team as they manage structure, feedback, and coordination. Progress happens when communication stays open and expectations stay clear.
      </p>
      <p>
        To new and growing criminals: reputation is built over time. Every scene contributes to how others perceive your crew. Make those scenes count.
      </p>

      <h3>Official Crew Process & Expectations</h3>
      <p>
        Official crew standards and process expectations remain active and enforced. If your crew is applying, reapplying, or expanding, make sure your structure, leadership, and roleplay direction align with current expectations before submission.
      </p>
      <p>
        Additionally, we wanted to thank Rowan and Avryn for their contributions to the crew team and wish them well.
      </p>
      <p>
        Next up, we are actively filling in the crew team with some fresh faces and ideas. These members will be contributing to official crew needs.
      </p>
      <p>
        As part of those fresh faces, we have collected feedback from several community members in the criminal world as well as our counterparts on the LEO side. We are refining our process of how things will work going forward.
      </p>
      <p>
        We appreciate all the feedback as it allows us to reflect and refocus our efforts to better serve the community.
      </p>

      <h2>🚓 Department Manager Things</h2>
      <p>
        Emergency Services are still one of the biggest pieces of what makes the city feel alive. The way trainings are run, how consistent patrols are, the quality of reports, and how departments work together all have a huge impact on RP overall. There’s a lot starting to build right now too. New server-wide storylines are kicking off, departments are hosting community events, and we’re seeing more lifers returning to the prison. The new penal code is also close to finished, and will roll out alongside other department manager updates.
      </p>
      <p>
        It’s a good time to take a step back and look at internal standards before the wipe. Are scenes being handled smoothly? Is communication holding up under pressure? Are newer members actually getting the support they need? And realistically, what needs to change moving forward? There’s a lot happening behind the scenes already, and staff will be sharing more soon. The new penal code is close to finished, among other things.
      </p>
      <p>
        Leadership sets the tone, and that tone shapes the culture. In the end, that shapes the experience for everyone.
      </p>
      <p>
        The structure and professionalism you all bring is what keeps the city steady, even when everything else is going off the rails.
      </p>

      <h2>🧠 Reminder and Refresh: Quality Roleplay and Server Health</h2>

      <h3>Intentional Roleplay</h3>
      <p>
        Every action should generate opportunity. If it does not create story progression or engagement for others, reconsider it. Depth over speed. Quality over repetition.
      </p>

      <h3>Moderation SOP Updates</h3>
      <p>
        Over the past month, we reviewed server activity, community feedback, and internal moderation discussions. Based on that work, we have put additional SOPs in place for moderation to improve consistency and fairness in how incidents are handled.
      </p>
      <ul>
        <li><strong>Clear escalation paths</strong> for repeat disruptive behavior</li>
        <li><strong>Stronger documentation standards</strong> for major incidents and patterns</li>
        <li><strong>Consistent enforcement review</strong> across staff to reduce mixed messaging</li>
        <li><strong>Roleplay-first evaluation</strong> focused on whether behavior builds story or suppresses it</li>
      </ul>

      <h3>Side Note</h3>
      <p>
        ProductionRP is not an arena server.
      </p>
      <p>
        Being upset about actions taken against your character or your friends in character is completely valid roleplay. Retaliation, tension, and conflict are normal parts of RP and often drive some of the best stories on the server. That is not the issue.
      </p>
      <p>
        The issue begins when conflict turns into sustained and intentional disruption.
      </p>
      <p>
        ProductionRP will not support patterns such as organized "hell weeks", treating the city as a target-rich combat environment, robbing or attacking every player encountered purely for domination, escalating situations in ways that remove meaningful counterplay, or using numbers and aggression to make the server feel unplayable for others.
      </p>
      <p>
        When roleplay becomes focused on making the experience miserable rather than building story, it stops being roleplay and becomes disruption.
      </p>
      <p>
        ProductionRP is built around long-term progression, character development, and shared storytelling. If someone's version of winning is ensuring others stop logging in, that direction is fundamentally incompatible with the server.
      </p>
      <p>
        Conflict should create story, not choke it out.
      </p>
      <p>
        We understand emotions can run high after raids, arrests, or losses. That is part of the world we are building together. However, sustained retaliation designed to overwhelm, dominate, or shut down the wider playerbase is not acceptable.
      </p>
      <p>
        Going forward, repeated large-scale disruptive behavior will result in administrative action. Groups that consistently push the server toward hostility rather than storytelling will be addressed directly. The long-term health of the server will always take priority over any single crew, storyline, or playstyle.
      </p>
      <p>
        ProductionRP exists for collaborative storytelling. If your goal is to build tension, conflict, and meaningful RP, you have nothing to worry about. If the goal is to turn the server into an arena, this is not the place for it.
      </p>

      <h2>🌟 Community Highlights and Appreciation</h2>
      <p>
        The city continues to thrive because people show up and invest. From structured department scenarios to spontaneous street interactions, from business launches to crew conflicts, the ecosystem only works when participation remains consistent.
      </p>
      <p>
        If you hosted an event, trained a recruit, planned a heist, ran medical scenes, opened your doors consistently, or simply gave someone meaningful RP this month, you contributed to the larger picture.
      </p>
      <p>
        That matters more than you think.
      </p>

      <h2>🎗️ Awareness Corner</h2>
      <p>
        This part of the year can feel transitional. Energy shifts. Activity patterns change. Continue to approach each other with patience and maturity both in character and out.
      </p>
      <p>
        If issues arise, use structured communication. Open tickets. Have conversations. Avoid public escalation. Professional handling of conflict preserves community health.
      </p>
      <p>
        February built pressure. March builds momentum. Stay intentional. Stay strategic. Keep evolving. We will see you in the city.
      </p>
      <p>
        Thank you for your patience, your passion, and for helping make PRP what it is. We appreciate every one of you—whether you’re building, roleplaying, supporting, or just showing up. This is your city as much as ours.
      </p>
    `
  },
  {
    id: '2025-12-2026-01',
    monthLabel: 'December 2025 / January 2026',
    content: `
      <div class="pulse-hero">
        <h1>New Year - New Pulse!</h1>
      </div>

      <p>
        As December rolled in, the city lit up and began winding down for the year… or at least pretending to. Between holiday cheer, questionable sweaters, seasonal chaos, and late-night antics, PRP stayed buzzing. Now, as we move through January, that energy carries forward into fresh starts, renewed motivation, and new stories taking shape.
      </p>
      <p>
        Whether you spent the season celebrating in RP, causing festive mischief, reconnecting with others, or simply enjoying the atmosphere, these past two months have been about connection, creativity, and closing one chapter while opening the next together.
      </p>

      <h2>🧰 Dev Things</h2>
      <p>
        The development team has continued pushing forward throughout the holiday season and into the new year. While things naturally slow down around the holidays, meaningful progress has still been happening behind the scenes as plans continue to take shape for what is coming next.
      </p>
      <p>
        A huge thank you to the entire dev team for the time, effort, and care they consistently put into keeping PRP running smoothly and evolving, even during one of the busiest times of year.
      </p>

      <h3>📦 Inventory Updates</h3>
      <p>
        We're excited to share some major progress on the inventory overhaul! The work has been moving rapidly and we have some fantastic updates to show off.
      </p>
      <p style="padding: 12px; background: rgba(79, 195, 247, 0.08); border-radius: 6px; font-size: 0.95em;">
        <strong>Scale of Work:</strong> The 2.0 refresh branch currently includes over 10,450 changed files with more than 256,000 additions and 175,000 deletions. This represents a massive undertaking that touches nearly every aspect of the server's systems.
      </p>

      <h4>Store Teaser</h4>
      <p>
        We're also excited to show off the initial version of the new Store interface, making the process of buying items much easier. Gone are the days of slowly dragging one loaf of bread at a time and going through the purchase flow each time. This comes with a few changes to what you are used to seeing, but now you can search for the items you are looking for or browse the categories, and build up a shopping cart before making a single purchase.
      </p>
      <div style="margin: 20px auto; max-width: 800px;">
        <video controls style="width: 100%; border-radius: 8px;">
          <source src="https://media.productionrp.org/pulse/Store-Sequence.mp4" type="video/mp4">
          Your browser does not support the video tag.
        </video>
      </div>

      <h4>Inventory Teaser</h4>
      <p>
        We're going to show a few things regarding the inventory rework. A lot of the new updates have made things very nice to work with, and we can't wait to get this out as part of the upcoming refresh. Everything is proceeding at a rapid pace but we have a few things to show off. One of the nicest features is the shortcuts being built into the interface allowing you to do things quicker, and with less pain, such as:
      </p>
      <ul>
        <li>Quick moving items</li>
        <li>Quick splitting items with a shortcut</li>
        <li>Swap the position of two different kinds of items</li>
        <li>Drag items over a bag in your inventory with a shortcut to quickly add the item to that bag</li>
      </ul>
      <div style="margin: 20px auto; max-width: 800px;">
        <video controls style="width: 100%; border-radius: 8px;">
          <source src="https://media.productionrp.org/pulse/Inventory-Teaser.mp4" type="video/mp4">
          Your browser does not support the video tag.
        </video>
        <p style="margin-top: 10px; font-style: italic; opacity: 0.8; font-size: 0.9em;">
          Note: This recording was captured on an ultra wide monitor. The interface will be properly sized and optimized when it goes live.
        </p>
      </div>

      <h4>Better Tooltips</h4>
      <p>
        This part works better shown individually, but as part of the new inventory interface, we had the perfect opportunity to examine our tooltips. We are overhauling what you get when you hover over an item, allowing you to get more information in a format that is easier to examine. We want to provide the information you get about an item in a way that is more useful, providing quality of life improvements along the way. Such as being able to view the attachments on a weapon!
      </p>
      <div style="margin: 30px auto; max-width: 1100px; display: flex; flex-wrap: wrap; gap: 20px; justify-content: center; align-items: flex-start;">
        <div style="flex: 1; min-width: 280px; max-width: 350px;">
          <img src="https://media.productionrp.org/pulse/item-icons-dl.png" alt="Driver's License Tooltip" style="width: 100%; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.3);">
        </div>
        <div style="flex: 1; min-width: 280px; max-width: 350px;">
          <img src="https://media.productionrp.org/pulse/item-icons-firearm.png" alt="Firearm Tooltip" style="width: 100%; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.3);">
        </div>
        <div style="flex: 1; min-width: 280px; max-width: 350px;">
          <img src="https://media.productionrp.org/pulse/item-icons-phone.png" alt="Phone Tooltip" style="width: 100%; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.3);">
        </div>
      </div>

      <h3>🚗 Game Build & Vehicle Updates</h3>
      <p>
        We're currently working on a major game build update that will bring in new clothing and vehicles from GTA Online, expanding the options available across the city. (Yes outfits will be messed up but we intend to warn you well in advance before things go live so you can screenshot outfits.)
      </p>

      <h4>PD Fleet Overhaul</h4>
      <p>
        The current PD fleet has longstanding issues with the models that need to be addressed. We're replacing the existing vehicles with better quality models to fix these problems. The scope of this project is substantial - we're working on 150+ liveries just for these replacements alone, not including anything new we're adding.
      </p>
      <p>
        Beyond the liveries, every vehicle needs handling work done or refreshed, mods configured, and proper testing. Any new civilian cars we're bringing in from the GTA Online update also need handling work completed. It's a lot of work being done in tandem with the giant refresh that's coming.
      </p>
      <p style="padding: 12px; background: rgba(79, 195, 247, 0.08); border-radius: 6px; font-size: 0.95em;">
        <strong>Scale of Work:</strong> Across multiple repositories, this project involves nearly 6,000 changed vehicle files with over 155,000 additions, configuration work spanning 100+ changes, and approximately 12,000 lines of handling code adjustments. This is in addition to the 150+ liveries being created and tested.
      </p>
      <p>
        For some vehicles in the current fleet, we didn't have direct replacements available in the new fleet we've acquired. So we let PD officers vote on what should take their place. Here are a couple examples of what's coming:
      </p>
      <div style="margin: 20px 0;">
        <p><strong>Buffalo Hellfire → Sentinel</strong></p>
        <img src="https://live.staticflickr.com/65535/55010121860_e662e54f5a_o.gif" alt="Sentinel Replacement" style="max-width: 100%; border-radius: 8px; margin: 10px 0;">
        
        <p style="margin-top: 20px;"><strong>Sandstorm → Bison XL</strong></p>
        <img src="https://live.staticflickr.com/65535/55008981617_7b39c9b3e2_o.gif" alt="Bison XL Replacement" style="max-width: 100%; border-radius: 8px; margin: 10px 0;">
      </div>
      <p>
        When the new PD fleet goes live, we'll also be releasing civilian versions of these vehicles where they don't already exist. For example, the Sentinel will have a civilian version available on day one.
      </p>
      <p style="margin-top: 15px; padding: 15px; background: rgba(79, 195, 247, 0.1); border-left: 4px solid #4fc3f7; border-radius: 4px;">
        <strong>What's Next:</strong> We have additional new vehicles ready to go, but those will be introduced down the road, likely with the big refresh when Inventory 2.0 launches. Right now, the priority is fixing the current fleet and getting those replacements live. These things take time and work, and we appreciate your patience as we work through everything.
      </p>

      <h3>💝 Support PRP</h3>
      <p>
        If you want to support your fellow roleplayers, the server, and the work going into these updates, please consider purchasing a <a href="https://store.productionrp.org/category/support" target="_blank" rel="noopener noreferrer" style="color: #4fc3f7; text-decoration: none; font-weight: 600;">monthly supporter package</a>. Your support helps us purchase assets like the PD fleet vehicles, civilian cars, props, and other content that enhances the entire server for everyone. Every bit helps us continue improving and expanding what PRP has to offer.
      </p>
      <p style="margin-top: 15px;">
        Visit our store at <a href="https://store.productionrp.org/" target="_blank" rel="noopener noreferrer" style="color: #4fc3f7; text-decoration: none; font-weight: 600;">store.productionrp.org</a> to browse all available packages and support options.
      </p>

      <h2>🏙️ Civ Things</h2>
      <p>
        The city remains vibrant thanks to those who continue to step up and make use of available spaces, businesses, and ideas. With the BBB cleanup largely complete, we are seeing more intentional RP, stronger concepts, and more community-driven interactions across the map.
      </p>
      <p>
        To everyone opening doors, running consistent hours, hosting events, or simply creating places for people to gather, eat, shop, and interact - your efforts do not go unnoticed. Your consistency through December and into January helps keep the city feeling alive, connected, and purposeful.
      </p>

      <h2>💀 Crew Things</h2>
      <p>
        First and foremost, thank you to the official crew members who have been working closely with the crew team and moderation/administration directly to address concerns and improve interactions. This continued collaboration is important as we look to bring in new groups and welcome back returning or refreshed crews.
      </p>
      <p>
        We have begun reaching out to official crews to gather feedback on upcoming changes, including shifts in criminal mentality and crime execution. Your input and engagement are appreciated as we work toward improving overall criminal RP.
      </p>
      <p>
        Please continue to support our crew team. They consistently put in the effort to help make things happen in ways that are fun, engaging, and different for everyone involved.
      </p>
      <p>
        Lastly, welcome to all the new criminals joining the city. We look forward to working with you and watching you grow into the level of chaos we expect. Give the cops a run for their money, find your place, and make your mark. We are always watching.
      </p>

      <h2>🚓 Department Manager Things</h2>
      <p>
        To all Emergency Services personnel, thank you for everything you continue to do. From responding to calls to organizing trainings, events, and department moments, your dedication plays a critical role in keeping the city functional and immersive.
      </p>
      <p>
        Your consistency, teamwork, and professionalism - especially through the holidays and into the new year - are a major part of what holds PRP together.
      </p>
      <p>
        On another note, we've begun working on bringing the FAA to life. More details to come as development continues.
      </p>

      <h2>👥 Moderation Team Update</h2>
      <p>
        Following our moderation applications section from the November Pulse, we are pleased to welcome new additions to the moderation team.
      </p>
      <p>
        <strong>Tobias</strong> joins us as a new moderator. We're excited to have him on board and look forward to the perspective and dedication he brings to the team.
      </p>
      <p>
        <strong>Rowan</strong> has been officially promoted to the moderation team. He has been an invaluable part of our support structure for some time now, consistently putting in the hours reviewing tickets, assisting other team members, and managing the crew team with care and attention. His understanding of both the rules and the creative direction we want to foster makes him well-equipped to support the entire community moving forward.
      </p>
      <p>
        We are also welcoming back <strong>AceBender</strong> and <strong>Servo</strong>, both of whom have served as moderators previously. Their experience and commitment to fostering meaningful roleplay and character-driven storytelling will be valuable as we continue prioritizing narrative depth and immersive experiences across the server.
      </p>
      <p>
        Please join us in welcoming and supporting the moderation team as they work to maintain a healthy, engaging, and story-focused environment for everyone.
      </p>

      <h2>🧠 Reminder and Refresh: Quality Roleplay and Server Health</h2>
      
      <h3>Intentional Roleplay</h3>
      <p>
        As we move into the new year, this is a reminder to keep roleplay intentional and purposeful. Every action should build a story or create opportunities for others to engage. Random actions done out of boredom often shut RP down rather than enhance it. Slow things down, communicate, and allow scenes to develop naturally.
      </p>

      <h3>Leaving Scenes</h3>
      <p>
        If you enter a vehicle or home because a door was unlocked or you had a key, you must leave a scene behind. This gives the owner something to follow up on in RP and helps them understand what occurred. Examples include scattered papers, signs of rummaging, or small details like a strand of hair. This reduces confusion, tickets, and staff time spent reviewing logs.
      </p>

      <h3>Prop Placement</h3>
      <p>
        Please continue to be mindful and respectful with prop placement. Creativity is encouraged, but excessive props can cause rendering and performance issues for others in the area. Keep usage reasonable and intentional. We want to avoid imposing restrictions, but server health will always take priority if needed.
      </p>

      <h3>Overuse of the F5 View Players Key</h3>
      <p>
        This has been stated before and needs to be reiterated. Continued misuse of the F5 view players function has been observed. If this is bound to another keybind used for unrelated actions, it must be changed. This is the final warning. Further misuse will result in being pulled into a ticket and receiving points.
      </p>

      <h3>Talking While Downed</h3>
      <p>
        When your character is downed or injured, your roleplay should reflect that state. While having conversations is fine, you need to sound injured - not yelling, not carrying on full conversations at normal volume. Your voice should convey pain, weakness, or struggle depending on the circumstances. Similarly, your actions should reflect your condition - you should not be performing emotes that suggest you're fine or acting in ways that contradict being incapacitated. If you're downed, you're not in a position to be loud, energetic, or physically active. Keep it realistic and immersive for everyone involved in the scene.
      </p>

      <h2>🌟 Community Appreciation</h2>
      <p>
        December and January offer a perfect moment to reflect on what this community continues to bring to PRP. From large-scale events to quiet character moments, intense scenes to absolute chaos, the city thrives because of the people who consistently show up and invest in the world together.
      </p>
      <p>
        To the event hosts, trainers, organizers, medics, officers, criminals, civilians, and everyone in between - thank you. Every interaction and every effort contributes more than you may realize.
      </p>

      <h2>🎗️ Awareness Corner</h2>
      <p>
        The holidays and the start of a new year can be joyful, but they can also be challenging for some. As we move through January, continue to be patient and kind with one another both in character and out. A little understanding goes a long way, and community starts with how we treat each other.
      </p>
      <p>
        Thank you for closing out another year and stepping into a new one with PRP. We look forward to the stories ahead, the opportunities to come, and continuing to build this community together. Stay warm, stay safe, and we will see you in the city.
      </p>
    `
  },
  {
    id: '2025-11',
    monthLabel: 'November 2025',
    content: `
      <div class="pulse-hero">
        <h1>🍂 November Pulse 🍁</h1>
        <p>Tis the Season to everyone from Production!</p>
      </div>

      <p>
        As we barrel toward the end of the year and those holiday months start rolling in fast, the city is buzzing with energy. Word on the street is that the grinchy menace himself, Jingles, has been spotted lurking around again. So keep your eyes open, keep your cocoa warm, and prepare yourselves… holiday chaos is brewing.
      </p>

      <h2>🧰 Dev Things</h2>
      <h3>🎉 Welcome New Team Members!</h3>
      <p>
        We're thrilled to welcome two incredibly talented individuals to our development team:
      </p>
      <p>
        <strong>Need_Ammo</strong> has joined us as our new mapping specialist! They'll be working on various mapping tasks, improvements, and creations as they learn the ropes. Fresh perspective and creative energy are exactly what we need to keep our world feeling vibrant and engaging.
      </p>
      <p>
        <strong>Westly</strong> is picking up the much-needed vehicle handling work that's been sitting dormant. He's been on our staff team for a bit now and with 2.0 in the works, we want everything to feel fresh when that launches. That includes giving our vehicle team the momentum to progress toward that goal.
      </p>
      <p>
        Getting vehicle development moving again is obviously a <strong>HUGE</strong> win since it's been dead in the water for too long. We are hoping to get the team in a position to start pushing out updates to test the waters soon.
      </p>

      <h3>📦 Inventory 2.0 Development Update</h3>
      <div style="background:#0a1828; padding:20px; border-left:6px solid #4fc3f7; margin:20px 0; color:#e1f5fe; border-radius:6px; max-width:100%;">
        <strong style="color:#4fc3f7; font-size:1.05em;">🚀 Major Progress Update</strong>
        <div style="margin-top:10px; line-height:1.6;">
          We know everyone's been waiting patiently for Inventory 2.0, and we're excited to share some significant progress updates!
        </div>
      </div>

      <h4>📋 Inventory Core System</h4>
      <p>
        <strong>Core functionality is complete!</strong> We're currently in the final phase of hooking the new system up to FiveM and getting everything properly integrated. The inventory system is largely feature complete, with the remaining work shifting towards setting up new items and preparing existing content for the upcoming wipe.
      </p>
      <p>
        While progress has been steady, we've encountered some FiveM-specific quirks that required creative solutions, but those challenges are behind us now and development is moving quickly again.
      </p>

      <h4>🏪 Revolutionary Store System</h4>
      <p>
        Say goodbye to dragging items one by one! Our new store interface is nearly complete and features:
      </p>
      <ul>
        <li><strong>Search functionality</strong> - Find exactly what you need instantly</li>
        <li><strong>Category browsing</strong> - Browse items by type for easier shopping</li>
        <li><strong>Shopping cart system</strong> - Add multiple items and buy them all at once</li>
        <li><strong>Single transaction checkout</strong> - No more swiping your card 15 times for a cookout!</li>
      </ul>
      <p>
        The remaining work involves setting up permissions for Emergency Services and other restricted shops, but the core shopping experience is ready to revolutionize how you interact with businesses.
      </p>

      <h4>🔨 Enhanced Crafting System</h4>
      <p>
        We're building a completely reimagined crafting experience that's more engaging and flexible than ever before:
      </p>
      <p><strong>Recipe Discovery System:</strong></p>
      <ul>
        <li><strong>Essential recipes</strong> - Basic items you'll know from the start</li>
        <li><strong>Discoverable recipes</strong> - Find books and magazines in stores or discarded bins</li>
        <li><strong>Experimental crafting</strong> - Some recipes can only be discovered through trial and error</li>
        <li><strong>Connected crafting stations</strong> - Stations will link together for more complex creations</li>
      </ul>
      <p>
        You might notice some recipe lists look shorter initially - this is intentional! We're reworking many recipes, removing ones that don't make sense, and adding many more discoverable options to create a more immersive and engaging crafting experience.
      </p>

      <h4>⌨️ New Shortcuts!</h4>
      <p>
        Coming in Inventory 2.0, we're adding several shortcuts for quick actions to save time when moving items around or generally interacting with the inventory. Everyone knows about double click to use an item, but coming soon:
      </p>
      <ul>
        <li><strong>Shift + Click:</strong> Quick move an item to the currently open container or ground</li>
        <li><strong>Shift + Click while dragging:</strong> Drop the item in that container while dragging over it</li>
        <li><strong>Alt + Left Click:</strong> Split off a single item from a stack</li>
        <li><strong>Alt + Right Click:</strong> Split the item stack exactly in half</li>
      </ul>
      <p>
        These shortcuts will make inventory management much faster and more intuitive, especially when organizing large amounts of items or transferring between containers.
      </p>

      <h4>🎬 Coming Very Soon</h4>
      <p>
        We'll be putting together a comprehensive teaser for Inventory 2.0 showcasing all these features in their final form. <strong>Look for that in next month's Pulse as Inventory gets pushed across the finish line!</strong>
      </p>
      <p>
        Thank you for your continued patience as we work to deliver a system that will fundamentally improve how you interact with items, businesses, and crafting in PRP.
      </p>

      <h3>🔍 We're Actively Looking for Talented Developers!</h3>
      <p>
        Speaking of team growth - we're actively seeking talented developers, modelers, handling developers, and more to join our ranks. Here's the exciting part: we have specialized tools that allow you to focus on exactly what you're passionate about.
      </p>
      <p>
        Want to work on vehicle handling? We can set you up to work exclusively on that without worrying about a bunch of other code. Interested in clothing design and modeling? We have isolated environments for that too. Love mapping? We've got you covered.
      </p>
      <p>
        If you're a coding developer with broader skills, we definitely want to hear from you as well. Whether your passion is:
      </p>
      <ul>
        <li><strong>Vehicle Handling & Physics</strong></li>
        <li><strong>3D Modeling & Clothing Design</strong></li>
        <li><strong>Mapping & Environment Design</strong></li>
        <li><strong>Full-Stack Development</strong></li>
        <li><strong>UI/UX Design</strong></li>
        <li><strong>Game Logic & Systems</strong></li>
      </ul>
      <p>
        We have tools and workflows that let you focus on what you love most. No need to be overwhelmed by the entire codebase - just dive into your specialty and make an impact.
      </p>

      <h4>🚗 Try Vehicle Handling Development Yourself!</h4>
      <div style="background:#1a1a2e; padding:20px; border-left:6px solid #00d9ff; margin:20px 0; color:#e0f7ff; border-radius:6px; max-width:100%;">
        <strong style="color:#00d9ff; font-size:1.05em;">🛠️ Learn Before You Apply</strong>
        <div style="margin-top:10px; line-height:1.6;">
          Interested in vehicle handling but not sure if it's for you? We believe in providing the tools to help you explore! Our <strong>live vehicle handling development tool</strong> is publicly available so you can experiment, learn, and see if it's something you're genuinely passionate about.
        </div>
      </div>
      <p>
        <strong>Download our Vehicle Debug Tool:</strong> <a href="https://github.com/kerminal/vehicleDebug" target="_blank" rel="noopener noreferrer" style="color:#00d9ff; font-weight:600;">👉 GitHub Repository</a>
      </p>
      <p>
        Set it up in your own dev server, play around with vehicle physics, tweak handling values in real-time, and discover if vehicle development is something that excites you. Learn at your own pace, experiment freely, and have fun with it. If you find yourself genuinely interested after exploring the tool, we'd love to have you apply to join our team.
      </p>
      <p>
        No commitment required to try it out. Just pure learning and exploration.
      </p>

      <p>
        Ready to contribute to the future of PRP? Apply here: <a href="https://core.productionrp.org/developerapplication" target="_blank" rel="noopener noreferrer">👉 Development Team Application</a>
      </p>

      <h3>📋 Moderator Applications - Fresh Start!</h3>
      <p>
        We've cleared out all old and pending moderator applications to start fresh. If you're interested in helping out with ticket management and player moderation, we encourage you to apply!
      </p>
      <p>
        Our moderation team plays a crucial role in maintaining the quality of roleplay and community standards that make PRP special. If you're passionate about fostering great RP, helping resolve conflicts, and supporting fellow community members, we'd love to have you on the team.
      </p>
      <p>
        <strong>What we're looking for:</strong>
      </p>
      <ul>
        <li>Fair and level-headed decision making</li>
        <li>Strong communication skills</li>
        <li>Understanding of RP principles and community standards</li>
        <li>Patience and empathy when dealing with player issues</li>
        <li>Commitment to maintaining a positive community environment</li>
      </ul>
      <p>
        Interested in joining our moderation team? Apply here: <a href="https://core.productionrp.org/staffapplication" target="_blank" rel="noopener noreferrer">👉 Staff Team Application</a> (just select moderator when applying)
      </p>

      <p>
        Side note: Applications for all roles have been cleared out for a fresh slate.
      </p>

      <h3>⚠️ Important Reminder: Real-Life Branded Content Policy</h3>
      <div style="background:#2d1b17; padding:20px; border-left:6px solid #ff6b35; margin:20px 0; color:#ffb399; border-radius:6px; max-width:100%;">
        <strong style="color:#ff6b35; font-size:1.05em;">🚨 FiveM Policy Compliance</strong>
        <div style="margin-top:10px; line-height:1.6;">
          We need to address an important policy issue: <strong>real-life branded content and assets cannot and will not be allowed on PRP.</strong> Any still remaining will have to be removed, no matter what.
        </div>
      </div>
      <p>
        FiveM's Terms of Service explicitly prohibit branded material, and this includes <strong>all</strong> content types: vehicles, props, inventory images, signage, mapping assets, and any other real-world branded materials. We cannot have real-world brand names, logos, or direct replicas of existing products from any manufacturers or companies.
      </p>
      <p>
        What <strong>is</strong> acceptable: Lore-friendly versions inspired by real-world items but with fictional branding, modified designs, and original names. Think of how GTA itself handles this - everything from cars to food brands to store signs are clearly inspired by real things but are transformed into something unique for the game world.
      </p>
      <p>
        We know some of you may have grown attached to certain branded content across the server, but we must comply with these policies to protect the server and community. Our development teams will work to ensure that any replacements maintain the quality, functionality, and visual appeal you've come to enjoy, just within the proper legal framework.
      </p>
      <p>
        This applies to: <strong>vehicles, props, inventory item images, business signage, mapping textures, clothing brands, and any other asset that features real-world branding.</strong>
      </p>
      <p>
        Thank you for understanding as we work to keep PRP compliant and sustainable for the long term.
      </p>

      <h2>🏙️ BBB Things</h2>
      <p>
        The BBB has officially finished cleaning up nearly all inactive businesses, creating fresh opportunities and access to new locations across the state. Three businesses are currently in the allocation program: <strong>Up-N-Atom Burger</strong>, <strong>Pearl's Seafood Market & Restaurant</strong>, and <strong>Kings Manufacturing</strong>. If you want more info about these exciting opportunities, reach out in character and learn how it works.
      </p>

      <h3>🎯 Featured Opportunities</h3>
      <p style="font-size:0.9em; color:#888; margin-bottom:15px; font-style:italic;">Click on any flyer to view full size</p>
      <div style="display:flex; justify-content:center; align-items:flex-start; gap:15px; margin:20px 0; flex-wrap:wrap;">
        <img src="https://i.gyazo.com/bd34e9b0a0a924f9706c8a546662a0a6.png" alt="Up-N-Atom Burger" onclick="event.stopPropagation();const overlay=document.createElement('div');overlay.style.cssText='position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.8);z-index:9998;display:flex;align-items:center;justify-content:center;cursor:pointer';const close=document.createElement('div');close.innerHTML='✕';close.style.cssText='position:absolute;top:20px;right:30px;color:white;font-size:40px;font-weight:bold;cursor:pointer;z-index:10000';const img=document.createElement('img');img.src=this.src;img.style.cssText='max-width:90vw;max-height:90vh;border-radius:8px;box-shadow:0 4px 20px rgba(0,0,0,0.5)';overlay.appendChild(close);overlay.appendChild(img);document.body.appendChild(overlay);overlay.onclick=()=>overlay.remove();close.onclick=()=>overlay.remove();" style="flex:1; min-width:200px; max-width:300px; width:100%; border-radius:8px; box-shadow:0 2px 8px rgba(0,0,0,0.12); cursor:pointer; transition:transform 0.2s;" />
        <img src="https://i.gyazo.com/abece2f0208f06d6cc69619b3511920a.png" alt="Pearl's Seafood" onclick="event.stopPropagation();const overlay=document.createElement('div');overlay.style.cssText='position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.8);z-index:9998;display:flex;align-items:center;justify-content:center;cursor:pointer';const close=document.createElement('div');close.innerHTML='✕';close.style.cssText='position:absolute;top:20px;right:30px;color:white;font-size:40px;font-weight:bold;cursor:pointer;z-index:10000';const img=document.createElement('img');img.src=this.src;img.style.cssText='max-width:90vw;max-height:90vh;border-radius:8px;box-shadow:0 4px 20px rgba(0,0,0,0.5)';overlay.appendChild(close);overlay.appendChild(img);document.body.appendChild(overlay);overlay.onclick=()=>overlay.remove();close.onclick=()=>overlay.remove();" style="flex:1; min-width:200px; max-width:300px; width:100%; border-radius:8px; box-shadow:0 2px 8px rgba(0,0,0,0.12); cursor:pointer; transition:transform 0.2s;" />
        <img src="https://i.gyazo.com/5954823606d3d319627c2de8d52cecae.png" alt="Kings Manufacturing" onclick="event.stopPropagation();const overlay=document.createElement('div');overlay.style.cssText='position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.8);z-index:9998;display:flex;align-items:center;justify-content:center;cursor:pointer';const close=document.createElement('div');close.innerHTML='✕';close.style.cssText='position:absolute;top:20px;right:30px;color:white;font-size:40px;font-weight:bold;cursor:pointer;z-index:10000';const img=document.createElement('img');img.src=this.src;img.style.cssText='max-width:90vw;max-height:90vh;border-radius:8px;box-shadow:0 4px 20px rgba(0,0,0,0.5)';overlay.appendChild(close);overlay.appendChild(img);document.body.appendChild(overlay);overlay.onclick=()=>overlay.remove();close.onclick=()=>overlay.remove();" style="flex:1; min-width:200px; max-width:300px; width:100%; border-radius:8px; box-shadow:0 2px 8px rgba(0,0,0,0.12); cursor:pointer; transition:transform 0.2s;" />
      </div>

      <p>
        We also want to recognize everyone who has been using empty buildings to run their own unique businesses and those of you already established keeping the city fed, dressed, caffeinated, tattooed, and stocked on all your favorite items. The BBB sees your hard work and your creativity. Keep going. You are making the city feel alive.
      </p>

      <h2>💀 Crew Things</h2>
      <p>
        Cleanup of inactive crews has been completed. This means new and returning crews now have plenty of opportunity to stake your claim in the city and make your name known.
      </p>
      <p>
        We want to recognize and thank the crews who have been part of our community and have since moved on. We appreciate the variety of roleplay they brought to our criminal underworld. As we move forward and new faces appear, the crew team is excited to welcome you into the family. We're looking forward to the fresh and creative storylines you'll bring to the city.
      </p>
      <p>
        Next, after many discussions in our ticket system, I'd like to send a special message to our remaining active crews. First, <strong>thank you for sticking with us and making an effort to increase your activity.</strong> With that increase, we've identified a few areas we'd like to improve. We are currently gathering feedback from our LEO counterparts on what types of crimes they would like to see more—or less—of at this time.
      </p>
      <p>
        We will be sending a separate message soon with guidance on how we can expand our criminal roleplay, incorporate this feedback, and continue to build a dynamic underworld. Once again, thank you for being here. Welcome back to our experienced crews, and welcome into our new organized-crime enjoyers. The crew team is excited to work with all of you to create engaging and fun criminal RP.
      </p>

      <p>
        ProductionRP offers some of the most authentic and immersive gang roleplay you'll find. Our criminal underworld is built on deep storytelling, territorial conflicts, complex relationships, and the constant tension between risk and reward. This isn't just about shootouts and chaos - it's about building a legacy, creating fear and respect, and writing stories that people will remember.
      </p>
      
      <p><strong>What makes PRP gang RP special:</strong></p>
      <ul>
        <li><strong>Territory & Turf Wars:</strong> Fight for control of neighborhoods, establish dominance, and defend what's yours</li>
        <li><strong>Complex Criminal Economy:</strong> Drug operations, smuggling networks, protection rackets, and underground businesses</li>
        <li><strong>Rival Dynamics:</strong> Long-term feuds, shifting alliances, and the politics of street life</li>
        <li><strong>Law Enforcement Tension:</strong> Cat and mouse games with police, high-stakes chases, and the constant threat of consequences</li>
        <li><strong>Character Development:</strong> Rise through the ranks, build your reputation, and become a legend on the streets</li>
        <li><strong>Authentic Culture:</strong> Each crew brings their own style, colors, codes, and traditions to the city</li>
      </ul>

      <h3>🎯 What We're Looking For & How to Apply</h3>
      <p>
        We want crews that bring <strong>quality roleplay</strong> and <strong>consistent activity</strong> to the criminal landscape. Your crew should have:
      </p>
      <ul>
        <li>A compelling backstory and clear identity that sets you apart</li>
        <li>Active leadership committed to driving storylines and member engagement</li>
        <li>Members who understand that great gang RP is about more than just violence</li>
        <li>Willingness to create opportunities for other players - both criminal and civilian</li>
        <li>Respect for the roleplay environment and community standards</li>
      </ul>

      <p>
        Think your crew has what it takes to make waves in the city? We're actively reviewing applications from groups ready to bring their vision to life. Official crew status comes with recognition, support from our crew management team, and access to exclusive benefits that set you apart from the rest.
      </p>
      
      <p><strong>Official Crew Benefits:</strong></p>
      <ul>
        <li><strong>Custom Clothing & Gear:</strong> Unique crew-specific clothing, accessories, and visual identity items</li>
        <li><strong>Exclusive Criminal Activities:</strong> Access to crew-only heists, operations, and storylines</li>
        <li><strong>Territory Recognition:</strong> Official acknowledgment of your crew's turf and influence</li>
        <li><strong>Crew Management Support:</strong> Direct line to staff for events, conflicts, and development</li>
        <li><strong>Priority Consideration:</strong> First access to new criminal content and features</li>
        <li><strong>Custom Props & Assets:</strong> Crew-specific items, vehicles, and equipment when appropriate</li>
      </ul>
      
      <p>
        <strong>Apply here:</strong> <a href="https://core.productionrp.org/crewapplication" target="_blank" rel="noopener noreferrer">👉 Crew Application</a>
      </p>
      <p>
        Show us your backstory, your vision for the crew, and how you plan to contribute to the criminal ecosystem. We're looking for crews that will elevate the standard of gang roleplay and create memorable experiences for everyone they interact with.
      </p>

      <h2>📝 Community Feedback Collection Initiative</h2>
      <p>
        We've recently launched comprehensive feedback collection efforts for our <strong>MDT</strong>, <strong>Organization System</strong>, and <strong>Core Systems</strong> to gather valuable insights for future development, adoption, changes, and updates.
      </p>
      <p>
        <strong>Our Department Managers</strong> have been leading this initiative by designing comprehensive feedback forms for our key systems, then working with the development team to ensure technical feasibility. They've been facilitating the collection of feedback from Emergency Services regarding the tools that affect daily government operations, handling the entire pipeline from form creation to community outreach.
      </p>
      <p>
        <strong>The Development Team</strong> receives this carefully collected feedback from Department Managers and uses it to prioritize their work, guide future development efforts, and implement the system improvements that matter most to our community.
      </p>
      <p>
        Your feedback is crucial in shaping how these systems evolve and improve. We want to ensure that the tools and interfaces you use daily are as intuitive, efficient, and helpful as possible. Whether it's suggestions for new features, reporting bugs, or proposing workflow improvements - every piece of feedback helps us prioritize development efforts.
      </p>
      <p>
        Thank you to everyone who has provided input through the proper channels. The data we collect will directly influence upcoming updates and help us focus our development resources into features that people want the most.
      </p>
      <p>
        Stay tuned for more opportunities to provide input as we continue expanding these feedback initiatives to the broader community.
      </p>

      <h2>🧠 Reminder and Refresh: Low and No Quality Roleplay</h2>
      <h3>No Low Quality Roleplay</h3>
      <p>
        Low quality roleplay happens when players act outside what is reasonable for their character or the world around them. This includes behavior that breaks immersion, creates unfair advantages, or disrupts opportunities for others to interact.
      </p>
      <p>
        We specifically want to address the random shooting of people or locals simply because you are bored. <strong>Shooting without purpose or buildup kills RP instead of creating it.</strong> Everything you do should push story, not shut it down.
      </p>
      <p>
        Slow it down, think it through, and remember that every choice your character makes should feel like it belongs in the world they live in.
      </p>

      <h2>🎗️ Awareness Corner</h2>
      <p>
        November is not only about Thanksgiving for the people in the states. It is also a time to reflect on gratitude for our families, our communities, and especially our veterans. Take a moment this month to thank those who have served and to appreciate the people around you who make life brighter.
      </p>

      <h2>💝 A Personal Note</h2>
      <p>
        To everyone who continues to host events, lift morale, organize team moments, and show up daily to contribute, thank you. These gestures are just as meaningful as any major scene. The community thrives because of your heart, your creativity, and your willingness to show up and tell stories together.
      </p>

      <p style="font-weight:700; color:#ff8456; font-size:1.1em; text-align:center; margin:20px 0;">
      Stay grateful, stay kind, and keep being awesome, PRP! 🍂🦃
      </p>
    `
  },
  {
    id: '2025-10',
    monthLabel: 'October 2025',
    content: `
      <div class="pulse-hero">
        <h1>🎃 October Pulse – It's Spooky Season in PRP! 👻</h1>
        <p>Tricks, treats, and community highlights!</p>
      </div>

      <p>
        It's spooky season! Grab your favorite candy, and hit the streets for a little trick-or-treating fun. Staff will be running a few small events throughout the month, and we encourage you to jump in and host your own too. Whether it's a costume contest, haunted house, or some good old-fashioned chaos in the city, we love seeing your creativity come to life.
      </p>

      <h2>💗 Awareness Corner</h2>
      <p>
        October isn't just about ghosts and candy - it's also <strong>Breast Cancer Awareness Month</strong> and <strong>National Bullying Prevention Month</strong>. Take a moment to show some kindness, support, and understanding to those around you. A little compassion goes a long way, whether it's wearing pink in support, standing up for someone, or just checking in on a friend. Let's keep PRP a place where everyone feels welcome, supported, and respected.
      </p>
      <div style="background:#ff69b4; padding:20px; border-left:6px solid #ff1493; margin:20px 0; color:#fff; border-radius:6px; max-width:100%;">
        <strong style="color:#fff; font-size:1.05em;">💖 Breast Cancer Awareness</strong>
        <div style="margin-top:10px; line-height:1.6;">
          This month, we stand with all those affected by breast cancer. Whether you're a survivor, currently fighting, or supporting a loved one, know that our community is here for you. Together, we're stronger.
        </div>
      </div>
      <div style="background:#4169E1; padding:20px; border-left:6px solid #0000FF; margin:20px 0; color:#fff; border-radius:6px; max-width:100%;">
        <strong style="color:#fff; font-size:1.05em;">🛡️ Bullying Prevention</strong>
        <div style="margin-top:10px; line-height:1.6;">
          Kindness is free, but its impact is priceless. Stand up, speak out, and be the reason someone feels safe and supported. Every voice matters in creating a community where everyone belongs.
        </div>
      </div>

      <h2>🧰 Dev Things</h2>
      <h3>🍬 Trick or Treating</h3>
      <p>
        Trick or Treating is here for Halloween, but with a twist! This year, the candy you collect will be designed differently to encourage you to still visit and support our amazing food businesses. Instead of stocking up on candy that lasts forever, think of trick-or-treat goodies as fun temporary treats that complement rather than replace the fantastic food and drinks our business owners work hard to create and serve.
      </p>
      <p>
        Whether you're going solo or teaming up with friends, there are different rewards waiting for you! Hit the streets, and enjoy the Halloween fun but don't forget to grab a proper meal from your favorite local spots too!
      </p>
      <p>
        <strong>Plus, the Halloween store in Vinewood is coming back!</strong> Check it out for props and decorations to complete your spooky look. Nothing says Halloween like the perfect costume accessories to really sell your character's transformation.
      </p>
      <p style="font-style:italic;">
        <em>Guys be careful about excessive props - it'll make people lag, but we think we've vetted all items and they won't crash people.</em>
      </p>

      <h3>📦 Inventory 2.0 & Crafting System Progress</h3>
      <div style="background:#1a1a2e; padding:20px; border-left:6px solid #0bd1ff; margin:20px 0; color:#cfeffc; border-radius:6px; max-width:100%;">
        <strong style="color:#0bd1ff; font-size:1.05em;">Development Update:</strong>
        <div style="margin-top:10px; color:#d6eef6; line-height:1.6;">
          As we have been finalizing Inventory 2.0, we discovered some complexities that needed to be addressed before we move forward. Rather than build on top of problematic code, we're taking the time to fix these issues properly. The code had grown complex and difficult to maintain, with functions trying to handle too many tasks at once. We've been listening to the feedback and the want for faster releases - to enable this to happen, the foundation needs to be solid.
        </div>
      </div>
      <p>
        We want you to know that we're taking the time to do this upfront because it'll benefit us all in the long term. This is essentially breaking the back of the biggest technical challenge - once Inventory 2.0 is complete, everything after should be quicker because it's the key core component. Once this is in, we can accelerate all the other activities that build on it.
      </p>
      <p>
        <strong>What we're focusing on in this revision:</strong>
      </p>
      <ul>
        <li><strong>Readable & Maintainable Code:</strong> Making the codebase cleaner and easier to understand <em>(faster to add new features, quicker to onboard new developers, easier to build new systems or expand existing ones)</em></li>
        <li><strong>Solid Foundation:</strong> Keeping the excellent existing database structure while improving everything around it <em>(prevents future crashes and ensures stability as we add more content)</em></li>
        <li><strong>Proper Documentation:</strong> Ensuring future developers can easily understand and work with the system <em>(means faster bug fixes and quicker feature development)</em></li>
        <li><strong>Top-Tier User Experience:</strong> Creating an interface that's not just functional, but exceptional <em>(smoother gameplay and more responsive interactions for everyone)</em></li>
      </ul>
      <p>
        <strong>Timeline:</strong> Everything has been held up over Inventory 2.0 - once complete, we can start slam dunking other new fun stuff for the upcoming wipe as planned (see prior Pulse updates for wipe details).
      </p>
      
      <h4>🔧 Technical Improvements</h4>
      <p>For those interested in the technical details, here are some key improvements we're implementing:</p>
      <ul>
        <li><strong>Performance Optimization:</strong> Converting large memory stores to hash maps for better performance</li>
        <li><strong>Security Enhancements:</strong> Adding detection systems to catch and auto-ban exploiters</li>
        <li><strong>Reliable Caching:</strong> Improved central cache system to keep all players' inventories synchronized</li>
        <li><strong>Queue System:</strong> Preventing item duplication exploits by processing inventory actions in order</li>
        <li><strong>Client Prediction:</strong> Instant UI feedback while maintaining server authority for security</li>
      </ul>
      <p>
        <strong>This foundation will support not just the inventory, but all future systems and interactions. We're building it right the first time so it can grow with us for years to come.</strong>
      </p>

      <div style="display:flex; justify-content:center; margin:20px 0;">
        <img src="https://i.gyazo.com/2a52a99a25cb97ea4700e994758a9fed.png" style="max-width:600px; width:100%; border-radius:8px; box-shadow:0 2px 8px rgba(0,0,0,0.12);" />
      </div>
      <p style="text-align:center; margin-top:8px; font-style:italic;">
        Early development look at Inventory 2.0 - can you spot what's different?
      </p>

      <h4>🍳 Integrated Crafting System</h4>
      <p>
        The inventory system is actually on pace and even progressing past where it was before, especially with our new crafting implementation. We're building a smart, intuitive system based on solid design principles:
      </p>
      
      <p><strong>Recipe Discovery System:</strong></p>
      <ul>
        <li><strong>Base Recipes:</strong> Essential items like sandwiches and drinks that you know from the start</li>
        <li><strong>Learnable Recipes:</strong> Advanced recipes you can discover from books, NPCs, and other sources</li>
        <li><strong>Secret Recipes:</strong> Hidden combinations you have to discover for yourself through experimentation</li>
      </ul>

      <p><strong>Smart Crafting Station Network:</strong></p>
      <p>
        We're moving away from the old "third-eye everything" approach. Instead, our new crafting stations are intelligent - when you're at a primary bench like a stove, it automatically detects nearby related equipment (cutting boards, cheese graters, etc.) and unlocks additional recipes based on what's available. This creates a more immersive cooking experience where your kitchen setup actually matters for what you can create.
      </p>
      <p>
        This system encourages players to build proper workspaces while making crafting more intuitive and less clicking around to find the right station for each step.
      </p>

      <h3>👕 Clothing Development Pipeline</h3>
      <p>
        We're working hard to get our clothing development pipeline back up and running smoothly. While we're experimenting with some Halloween-themed additions, our main focus is building the system correctly to prevent crashes and ensure we don't lose any of the current clothing options you already have.
      </p>
      <p>
        Simply reverting to last year's Halloween state isn't an option since it would undo a lot of progress we've made with clothing stability and additions. We're taking a more careful approach this time around. We have struck gold with properly getting things in just need to work on more than one thing at a time now.
      </p>
      <p>
        <strong>Our priority:</strong> Build it right, keep what you have, and add new options safely. We'd rather take the time to do it properly than rush and cause issues for everyone's existing outfits.
      </p>

      <h3>🚗 Vehicle Development Team Recruitment</h3>
      <p>
        We're also working on getting vehicle development rolling again! We're actively looking for talented individuals to join our vehicle team, specifically for vehicle tuning/handling and additions. If you have experience or passion for vehicle mechanics, handling physics, or want to help expand our vehicle selection, we'd love to hear from you.
      </p>
      <p>
        Whether you're interested in <strong>coding development</strong>, <strong>clothing design</strong>, or <strong>vehicle development</strong>, we encourage all applications! Help us build the future of PRP by joining our development team.
      </p>
      <p>
        <strong>Ready to contribute?</strong> Apply here: <a href="https://core.productionrp.org/developerapplication" target="_blank" rel="noopener noreferrer">👉 Development Team Application</a>
      </p>

      <h2>🌟 Community Highlight</h2>
      <p>
        Big shoutout to Type Shit Publishing's Sam for creating the awesome card game <strong>Turf War Tactics</strong>! Be sure to find him them the city it's worth checking out.
      </p>
      <div style="display:flex; justify-content:center; margin:20px 0;">
        <img src="https://i.gyazo.com/7d2e837b964d2fe80d12b46d1e6c397a.png" alt="Turf War Tactics Card Game" style="max-width:500px; width:100%; border-radius:8px; box-shadow:0 2px 8px rgba(0,0,0,0.12);" />
      </div>
      <p style="text-align:center; margin-top:8px; font-style:italic;">
        We love seeing the creativity and initiative from our community members who take their roleplay beyond the game!
      </p>

      <h2>🏙️ What's Happening in the City</h2>
      
      <h3>🏢 Civ Things</h3>
      <p>
        Just like we mentioned in the last update, the BBB has been hard at work clearing out inactive businesses. That means there are now a number of open locations throughout the state that are accessible to everyone. While the BBB isn't currently accepting new business applications, they are watching for those who take the initiative to open and run their business. They want to see dedication, creativity, and consistency before extending an invite into the BBB. We love seeing how many of you are stepping up, trying new ideas, and breathing life into the community. Keep that creativity flowing!
      </p>

      <h3>💀 Crew Things</h3>
      <p>
        Our Crew Team has been busy planning and organizing events to keep things fun for both the city's dirty criminals and our hard-working LEOs. They've also been working behind the scenes to set things up for the future and make sure everything runs smoothly. A huge shoutout to Rowan and Kermis for being absolute legends and keeping the events rolling - you're killing it!
      </p>

      <h3>🚓 Department Manager Things</h3>
      <p>
        To all of our Emergency Services personnel, thank you. Your continued dedication to protecting and serving the city day in and day out does not go unnoticed. The work you do, often under immense pressure and challenging circumstances, is the backbone of our city's safety and stability.
      </p>
      <p>
        To those going the extra mile to host events, organize trainings, and lift morale, you make a massive difference. These moments of connection and teamwork are just as important as the calls you respond to. They remind everyone that behind every badge and uniform stands someone with heart, courage, and compassion.
      </p>
      <p>
        And a big thank you to everyone who continues to fly in daily and make PRP what it is, this community thrives because of you.
      </p>

      <h2>🧠 Reminder & Refresh: Community Respect & Escalations</h2>
      <h3>Be Respectful</h3>
      <p>
        Always be mindful of not only your character's choices and actions in the city, but also your behavior toward other community members on all PRP platforms. Everyone has a story to tell, not just your own.
      </p>
      <p>
        Do not use PRP's platforms to publicly advertise or privately promote a product, service, page, or group without admin approval. Keep your OOC emotions in check while in-game. Malding or excessive OOC frustration breaks immersion and disrupts the roleplay experience for everyone.
      </p>
      
      <h3>No Harassment or Discrimination</h3>
      <p>
        Bullying, discrimination, harassment, and harmful behavior will never be tolerated. This includes mistreatment or discriminatory language about a community member's age, gender, race, disability, sexual orientation, or religion. And yes, even jokingly using slurs or derogatory terms toward friends is not okay.
      </p>
      
      <h3>Escalations</h3>
      <p>
        Your character should not be jumping straight to violence. There needs to be buildup and context leading into it. Whether you're a cop, criminal, or civ, pulling a weapon right away cuts off potential RP that could've made for a better story. Slow down, build it up, and enjoy the story that unfolds.
      </p>

      <h2>🚁 Helicopters</h2>
      <p>
        We know it used to be frowned upon to steal helicopters, but things have changed a bit. You can now do it if it makes sense for your character and fits within RP. Just keep these conditions in mind:
      </p>
      <ul>
        <li><strong>Remember the restricted area rule</strong></li>
        <li><strong>Don't use it to be a menace</strong> (you know what we mean)</li>
        <li><strong>Don't use it for crime</strong> (i.e. no banks, no prison breaks, robberies..etc)</li>
        <li><strong>If you're caught by LEOs, expect consequences</strong> (bracelets and all)</li>
        <li><strong>Don't make it a habit</strong> - if we notice the same person or group repeatedly joyriding in the sky, this freedom might disappear</li>
      </ul>
      <p>
        Have fun, make sure it makes sense for your character, and keep it in good RP.
      </p>

      <p style="font-weight:700; color:#ff8456; font-size:1.1em; text-align:center; margin:20px 0;">
        Stay spooky, stay kind, and keep being awesome, PRP! 👻💜
      </p>
    `
  },
  {
    id: '2025-08-09',
    monthLabel: 'August & September 2025',
    content: `
      <div class="pulse-hero">
        <h1>📢Pulse Update📢</h1>
        <p>We’ve got a lot to catch you up on!</p>
      </div>

      <h2>Development</h2>
      <div style="background:#071223; padding:20px; border-left:6px solid #0bd1ff; margin:20px 0; color:#cfeffc; border-radius:6px; max-width:100%;">
        <strong style="color:#0bd1ff; font-size:1.05em;">Personal note from Kerm:</strong>
        <div style="margin-top:10px; color:#d6eef6; line-height:1.6;">
          I love working on Production RP and appreciate everyone who plays, supports, and helps shape the server. I'm currently the primary developer maintaining/creating many systems while balancing work and life, so progress can sometimes be slower than we all want. A few team members took needed personal time in July/August, which also shifted schedules.
          Thank you for your patience - we’re focused on steady, meaningful progress and getting the major updates right rather than rushing releases.
        </div>
      </div>
      <p style="font-weight:700; color:#ff8456; font-size:1.06em; margin:14px 0 8px; letter-spacing:0.2px;">
        On to development and wipe details
      </p>
      <p>
        Our development progress has been slower than we'd like, primarily due to limited developer resources. We're a volunteer-driven team, and while we're passionate about Production RP, the demands of real life mean we can't always dedicate as much time as we'd want.
      </p>
      <p>
  The inventory system is nearing completion and release-ready state. Core features like click-to-move, item logic, and stacking are solid, and containers (bags and boxes) are pretty much done. We’re also looking into preserving vending machine functionality and integrating stores around the world. Important note: this refresh requires us to recreate items and item definitions for the new system - there is no backward compatibility with the old item database; items we want to keep will be rebuilt or reissued as part of the migration.
      </p>
      <p>
        We're aiming to reach an alpha/beta phase as soon as possible to prepare for the upcoming wipe. We want to have the economy in a relatively ready state for that. Currently, we're estimating about 6 months of development work remaining, but with additional help, we could accelerate that timeline significantly.
      </p>
      <p>
        To help us get there faster, we're actively looking for new developers to join our volunteer team. We're particularly seeking individuals proficient in LUA and/or TypeScript. TypeScript is especially crucial as most of our big resources, like the inventory system, are built with it. If you're skilled in these areas and passionate about Production RP, we'd love to have you on board.
      </p>
      <p>
        Interested? Check out our developer application: <a href="https://core.productionrp.org/developerapplication" target="_blank" rel="noopener noreferrer">👉 Apply Here to Join the Development Team</a>
      </p>
      <p>
        Subscriptions and financial support keep the servers online and development moving. If we can reach a point where we can pay contributors, we want to do that - but our immediate priority is keeping live roleplay stable and enjoyable while we finish work toward the planned wipe in about six months. Leadership prefers a single wipe once the new systems are ready (roughly six months), rather than multiple wipes. We can open a community vote on timing; if you'd prefer narrower input we can limit the vote to supporters. Supporting us helps cover hosting and lets us invest more in the project.
      </p>      <!-- Inventory Video -->
      <div style="width:100%; position:relative; padding-bottom:56.25%; margin:24px 0;">
        <iframe src="https://streamable.com/e/xj4nds" frameborder="0" allow="autoplay; fullscreen" style="position:absolute; top:0; left:0; width:100%; height:100%;"></iframe>
      </div>
      <p style="text-align:center; margin-top:8px;">Latest Inventory System Teaser</p>
  <p style="margin:18px 0; font-size:1.08em; color:#b6f7c6; font-weight:600;">The new inventory system and containers you see here are far more than just a UI upgrade - they’re the technical foundation for everything that comes next. This system is the backbone for all jobs, items, storages, and interactions in the server. Every future feature, job, and gameplay mechanic will build off this core, making it one of the most complex and important projects we’ve ever tackled. Huge thanks to our devs for pushing through the technical challenges and laying the groundwork for the next era of Prodcution RP. Stay tuned for more updates!</p>
        <p style="margin:14px 0; font-size:1.05em; color:#ffe6a0; font-weight:500;">We’re also excited to announce that <strong>Seri</strong> has rejoined the coding side of the development team! Seri originally created our laptop system and is a wonderful developer. We’re thrilled to have her back and helping us reach our goals.</p>
      <h2>🏢 BBB (Better Business Bureau)</h2>
      <p>
        The BBB has been working through and removing inactive businesses. As those spots open up, they’re being made accessible to the general public.
        If you’ve been considering business ownership, now’s the perfect time to jump in. We want to see the effort, creativity, and consistency you put into operating your business before extending that invite to officially become part of the BBB.
      </p>

      <h2>👥 Crews</h2>
      <p>
  Welcome Rowan to the Crew Manager position. Rowan has been taking the lead on all things crew related alongside Armeros. Give them your support and appreciation for the work they do for our crews. These two have been working to keep things running and finding adventures for our active criminals. Their ideas have been fun and engaging for everyone - we encourage you all to give them more opportunities to shine and create some fun times for all.
      </p>
      <p>
        We’re putting in work to bring more activities and opportunities to crews across the city. At the same time, we’re reviewing all current official crews and their activity. If you and your crew want to keep that official status, we need to see your presence, teamwork, and roleplay in action. We’ve seen an uptick in LEO presence, and we want to provide them opportunities.
      </p>
      <p>
        Shoutout to the crews that have been driving the activity lately. There have been a few new crews that have really started coming into their own. We appreciate their growth in our criminal space and look forward to more!
      </p>

      <h2>🛠️ Department Managers</h2>
      <p>
        Some exciting updates from the Department Manager side of things:
        Adriayn has stepped into the Department Manager role. He started with us in Support Staff and has quickly proven himself as a huge help with everything Department Manager-related.
      </p>
      <p>
  SCJ Freya has officially come out of retirement and is already making fast progress on the dockets. Keep your eyes open - things are moving!
      </p>

      <h2>⚖️ Reminder & Refresh: Roleplay Quality</h2>
      <p>
        We want to take a moment to refresh everyone on the expectations around Low-Quality Roleplay (Fail RP).
      </p>
      <h3>🚫 No Low-Quality Roleplay</h3>
      <p>
        Low-quality RP means playing outside of what would reasonably make sense for your character in RP. It breaks immersion, creates unfair advantages, and takes away from the high standards of roleplay we expect on Production RP.
        Here are the key reminders:
      </p>
      <ul>
        <li>Stay in-character at all times. (Exceptions only with moderator/admin approval.)</li>
  <li>Use only in-character terminology (“City,” “Taking a nap,” etc.) - never read OOC or Help chat out loud in-character.</li>
        <li>Hostile situations should be roleplayed thoroughly. Don’t escalate to immediate violence unless it makes sense for the story.</li>
        <li>Drama on social media alone is not enough to justify immediate PVP.</li>
        <li>Repeated retaliatory violence beyond a single instance is not allowed.</li>
        <li>Being arrested and charged is not enough justification to retaliate against law enforcement.</li>
        <li>Pocket wiping (taking everything from someone) is prohibited.</li>
        <li>No taunting (dancing, emoting) over downed players.</li>
        <li>No OOC terms in-character (e.g., “W cop/crim,” “L cop/crim,” “SOP check,” “RP check”).</li>
        <li>/me’s and /status are for visible actions only, not thoughts or feelings.</li>
        <li>Cop baiting for no storyline/reason is not allowed.</li>
        <li>Characters must:</li>
        <ul>
          <li>Fit a realistic, modern-day setting.</li>
          <li>Be 18+ with thorough development, motives, and reasons behind their actions.</li>
          <li>Have appropriate names (no mocking names, no roleplaying as real-life figures).</li>
          <li>No 24/7 body armor/masks as a permanent appearance (except medical masks with RP).</li>
          <li>Provide medical roleplay when injured (through /me’s, /do’s, or verbal descriptions).</li>
          <li>Pleading Not Guilty but then failing to communicate/follow up with DOJ is not acceptable.</li>
          <li>Do not insert yourself into situations that don’t involve you (e.g., hijacking a heist after cops arrive, interfering in shootouts between other groups).</li>
        </ul>
      </ul>
      <p>
        These rules are here to keep our community immersive, fair, and fun for everyone. Let’s keep the quality high and the stories engaging.
      </p>

      <h2>🎉 Shoutouts & Thank-Yous</h2>
      <p>
        Taking a moment we want to also highlight the shoutouts from the last couple months. We truly appreciate the community and shouting out each other is very much appreciated. So shoutout to you all for shouting out and to those that got the shout. Keep up being amazing and fun!
      </p>
      <h3>🚓 Police & Chase RP</h3>
      <ul>
        <li><strong>T Payne</strong> - shoutout for chase fun.</li>
        <li><strong>C. Anthony 972 | R. Tubbs</strong> - praised for ranger comms, skateboard RP, handling chaos with class.</li>
        <li><strong>Evie | 974 A. Wilson</strong> - recognized for K9 shifts, bravery, and great RP.</li>
        <li><strong>NoOne | E. Rossi</strong> - consistent shoutouts for fun RP and chase interactions.</li>
        <li><strong>StrykZ</strong> - K9 RP, funny/serious balance.</li>
        <li><strong>Duncan Robins</strong> - great cop roleplay in races/chases.</li>
        <li><strong>William Forsyth</strong> - praised for roleplay knowledge, Vengies chase, and comedy ("sausages at me!").</li>
        <li><strong>Danny A. Kinsly | D. O’Conner</strong> - fun pursuit, drive-through pickup mid-chase.</li>
        <li><strong>Tempest | Chief Ed Asher</strong> - RP chef/bartender moments during chase.</li>
        <li><strong>Isabelle May | Lily May</strong> - gave LEOs a long and fun pursuit.</li>
        <li><strong>Yuri Valent | Dean K.</strong> - fun chase and arrest.</li>
        <li><strong>Tessa | Sage | Tillie</strong> - multiple mentions for fun chases and great RP.</li>
        <li><strong>Kat | Ava | Jones | Mita | William Forsyth.</strong> - segway robbers.</li>
      </ul>
      <h3>🚗 Street Racers & Criminal RP</h3>
      <ul>
        <li><strong>Mr Lettuce</strong> - multiple mentions for races, fun energy, food pickup during pursuit.</li>
        <li><strong>BrianStevens</strong> - race RP and protecting Mirror Park.</li>
        <li><strong>Armeros1 | Kermis | Abraham 701</strong> - Mirror Park roleplay, wild chases, and cop interactions.</li>
        <li><strong>Amelia | Tommy</strong> - for street racing fun and banter.</li>
        <li><strong>F-55 | Infinity</strong> - shoutouts to fellow racers.</li>
        <li><strong>Jack Reign | E. Watson 514</strong> - praised for RP quality.</li>
        <li><strong>Dima Sergeyivich | Jack Bauer</strong> - recognized for chase roleplay.</li>
        <li><strong>Kat | Ava | Jones</strong> - chase RP, stabbing scenes, segway robberies.</li>
        <li><strong>Aleena | Maya | Maddison</strong> - involved in chase/stabbing RP.</li>
        <li><strong>Maggiras | Berger 501</strong> - always fun in RP.</li>
        <li><strong>banksK</strong> - multiple shoutouts for fights, fun, and crime RP.</li>
        <li><strong>Rowan</strong> - teamwork in crime arcs.</li>
      </ul>
      <h3>🎭 General RP & Community Fun</h3>
      <ul>
        <li><strong>Discount Jiggy/Jenkins</strong> - constant partner in banter and RP.</li>
        <li><strong>N. Cooper | J. Johnson | A. Green</strong> - recognized often for fun RP and roleplay energy.</li>
        <li><strong>Brittany Martin</strong> - happy return to shifts.</li>
        <li><strong>Warehouse duo</strong> - appreciated for chases.</li>
        <li><strong>Jason Rivers</strong> - fun, confusing, and hilarious interactions.</li>
         <li><strong>All LEOs</strong> - thanked several times for good police roleplay.</li>
         <li><strong>SAHA & SAFR teams</strong> - thank you for continuing to keep us all alive!</li>
        <li><strong>All roleplayers</strong> - general thanks for making the RP world fun.</li>
      </ul>
      <p>
  ✨ Again, thank you to everyone for making the RP community active, fun, and memorable. Each chase, race, crime, and interaction adds to the experience - and clearly, people notice and appreciate it!
      </p>
    `
  },
  {
    id: '2025-07',
    monthLabel: 'July 2025',
    content: `
      <div class="pulse-hero">
        <h1>🌟 The Pulse!</h1>
        <p>July 2025: A Summer of Change!</p>
      </div>
      <div class="pulse-tagline">
        <p>Map updates, interaction upgrades, and community-driven changes</p>
      </div>

      <p>
  Welcome back to another edition of <strong>The Pulse</strong> - your monthly wrap-up of all things ProductionRP. While we continue building long-term systems and planning our next big update, we’ve also pushed live some meaningful tweaks this month to keep things feeling fresh.
      </p>

      <h2>🧼 World Cleanup & Business Unlocks</h2>
      <p>
        We've done a sweep of older, unused business assets and re-opened several previously locked interiors. This helps improve performance, clears clutter, and gives players more options for creative RP.
      </p>
      <ul>
        <li><strong>Old business vending machines removed</strong></li>
        <li><strong>Cool Beans</strong> – vending machine moved per request</li>
        <li><strong>Red & Co. LTD</strong> – vending machine moved per request</li>
        <li><strong>Pretty In Pink</strong> – gift basket image fixed</li>
      </ul>
      <p><strong>Previously locked locations now accessible:</strong></p>
      <ul>
        <li>Rex’s Diner</li>
        <li>Pizza This</li>
        <li>Vape World</li>
        <li>Bean Machine – Legion Square</li>
        <li>Only Tows (now Popular St. Towing)</li>
        <li>Boop’s Scoops (now Frozen Cup)</li>
        <li>Pastime Lanes</li>
      </ul>
      <p>
        These spaces are now open for public use.
      </p>

  <h2>🚔 Org & Agency System - Expanding Access</h2>
      <p>
        The organization system rework is already live for BBB businesses as many of you know and love, complete with tablet-based controls, adjustable pay rates, and role-based permissions. This has proven successful and stable.. so next up is expanding that same framework.
      </p>
      <p>
        The next phase involves adapting this system for Emergency Services and government agencies, and then eventually making it available to the wider public for other player-run groups.
      </p>
      <p>
        This structure will give departments and groups more control, reduce staff dependency, and make managing teams much more seamless.
      </p>

      <h2>🛠️ Vehicle Development Tools Update</h2>
      <p>
        Our vehicle development team has had an in-house live handling editor tool, giving them direct access to vehicle dynamics data. They’ve also been granted pursuit mode parameters and related features they’ve been eagerly awaiting. This will allow them to fine-tune cop car handling and pursuit mode behavior, ensuring smoother, more balanced chases.
      </p>

  <h2>🍽️ Starvation Update - Live Now</h2>
      <p>
        Last month, the community voted <strong>189 to 60</strong> in favor of changing how starvation affects gameplay. That change is already live:
      </p>
      <ul>
        <li>Characters <strong>cannot be incapacitated</strong> from starvation (damage floors at 50%)</li>
        <li><strong>Sprinting and jumping are disabled</strong> while starving</li>
        <li>Eating or drinking restores normal behavior</li>
      </ul>
      <p>
        This gives players more time to react and prevents exploiting starvation to avoid death or reset combat scenarios.
        <br/><a href="https://discord.com/channels/888066725923418142/888071546894172240/1389904466639327242" target="_blank">🔗 View the vote</a>
      </p>
      <p>
        Although this feature was part of our broader refresh plans, we rolled it out early because it was low-risk, easy to test, and helps keep the experience from feeling stale. Expect more sneak-peek QoL drops when they’re ready.
      </p>

      <h2>🧥 Refreshed Clothing Commands</h2>
      <p>
        Several clothing commands have been updated for reliability across all clothing variants on the live server. The following are now confirmed working in full effect for all varients:
      </p>
      <ul>
        <li><code>/visor</code></li>
        <li><code>/hoodie</code></li>
        <li><code>/zip</code></li>
        <li><code>/tuck</code></li>
        <li><code>/sleeves</code></li>
        <li><code>/shades</code></li>
      </ul>
      <p>
        These commands now behave consistently with clothing logic across all genders and slots. Use them freely!
      </p>

      <h2>🔮 What’s Still Cooking: Big Update Work Continues</h2>
      <p>
        We know everyone’s eager for the big release - here’s what’s actively in development:
      </p>
      <ul>
        <li><strong>Brand new inventory system:</strong> core features are working, including click-to-move and item logic. Work continues on containers like bags and boxes, and we’re revisiting PRB shops (vending machines) and building the backend to support generic business creation, item structuring, and more. The UI is fully complete for the inventory - now we’re layering back in the unique PRP touches that make our inventory shine.</li>
        <li><strong>Overhauled job framework:</strong> we’re aiming to give both starter and specialty jobs new life through progression paths, inter-job synergy, and exploratory mechanics. More info to come as we finalize how everything links together.</li>
        <li><strong>Org/MDT permissions & tools:</strong> frameworks are looking to be expanded and rolled out to other groups as mentioned above.</li>
        <li><strong>New Interaction & General UI:</strong> cleaner logic, better performance, and revamped visuals. See the teaser below!</li>
      </ul>
      <!-- Video Teaser -->
      <div style="display:flex; justify-content:center; margin:24px 0;">
        <div style="width:80%; max-width:900px; position:relative; padding-bottom:56.25%;">
          <iframe src="https://streamable.com/e/3yjx63" frameborder="0" allowfullscreen style="position:absolute; top:0; left:0; width:100%; height:100%;"></iframe>
        </div>
      </div>
      <!-- Inventory Preview -->
      <div style="display:flex; justify-content:center; margin:24px 0;">
        <div style="width:80%; max-width:900px; position:relative; padding-bottom:56.25%;">
          <iframe src="https://streamable.com/e/dblc47" frameborder="0" allowfullscreen style="position:absolute; top:0; left:0; width:100%; height:100%;"></iframe>
        </div>
      </div>

      <h2>📢 Reminder & Refresh: Roleplaying Integrity and Fairness</h2>
      <p>
        We want to take a moment to refresh everyone on some key principles that help keep our community fair, immersive, and enjoyable for all: Powergaming, Metagaming, and Deathmatching. Please take a moment to read through the following reminders:
      </p>
      <h3>🔷 Powergaming</h3>
      <p>
        Excessive use of F5 falls under powergaming. While city population may be low at times, we should always RP as if the city is fully alive.
      </p>
      <p>
        F5 should only be used:
      </p>
      <ul>
        <li>When placing a player report to obtain an ID.</li>
        <li>If you’re LEO, to fine or jail the correct person.</li>
      </ul>
      <p>
        Using F5 to shortcut or avoid RP breaks immersion and can lead to action being taken.
      </p>
      <h3>🔷 Metagaming</h3>
      <p>
        This is gaining or using information/items outside of natural RP. Examples include:
      </p>
      <ul>
        <li>Sharing information between your own characters.</li>
        <li>Transferring houses, cars, money, items, etc. between your characters.</li>
      </ul>
      <p>
        Treat each character as a separate person, living their own life. What one knows or owns does not carry over to another.
      </p>
      <h3>🔷 Deathmatching</h3>
      <p>
        Attacking, downing, or harming another player (or locals) without valid RP reasoning or initiation is considered deathmatching.
      </p>
      <p>
        Always ensure proper interaction and RP build-up before engaging in violent actions.
      </p>
      <p>
        We know not every possible situation is listed-if we tried to list them all, our rules would be pages long and overwhelming. Instead, we trust that everyone in this community is capable of using sound judgment and upholding the integrity of RP.
      </p>
      <p>
        Let’s continue treating each other with respect and keeping RP fair, immersive, and enjoyable.
      </p>
      <p>
        Thank you all for being part of this community!
      </p>

  <p><strong>Until next time, stay safe and stay creative -</strong></p>
  <p><strong>- Kerm & The ProductionRP Team</strong></p>
    `
  },
  {
    id: '2025-06',
    monthLabel: 'June 2025',
    content: `
      <div class="pulse-hero">
        <h1>🌇 The Pulse!</h1>
        <p>Hitting the Halfway Point of 2025!</p>
      </div>
      <div class="pulse-tagline">
        <p>Fixes, features, community celebrations, and behind-the-scenes sneak peeks</p>
      </div>

      <p>
  We’re officially halfway through 2025 - time flies! This June, we already have or are pushing several quality-of-life updates, are currently celebrating Pride Month and observing Men’s Mental Health Month, and we’re continuing work on our biggest refresh ever.
      </p>

      <h2>🌈 Pride Month Appreciation</h2>
      <p>
        June marks Pride Month, a time to honor and uplift our LGBTQ+ community members. Your creativity, resilience, and vibrant stories enrich every corner of PRP. This month’s Pride Mapping overlay is our way of painting the city rainbow in solidarity with your journeys.
      </p>

      <h2>💙 Men’s Mental Health Month</h2>
      <p>
        In tandem with Pride, June also shines a light on Men’s Mental Health Month. We recognize the importance of open conversations about well-being, especially in a community built on storytelling and support. Thank you to everyone who’s shared their experiences or reached out to friends in need.
      </p>
      <p>
  Remember, strength comes from connection - if you ever need to talk, our community stands ready to listen. You’re never alone. 💪
      </p>

      <h2>🛑 Staff Update: Vehicle Development & Recent Conduct</h2>
      <p>
        We want to be transparent with our community about a significant recent change. Our former vehicle lead has been removed from the community following repeated instances of unprofessional conduct, including inappropriate use of access, aggression toward staff and players, leaking internal discussions, and the use of hate speech and personal attacks.
      </p>
      <p>
  To be clear, this decision wasn’t made lightly. It followed a long period of growing concerns - especially around vehicle exclusivity, promises not kept, and concerns regarding how custom vehicles were allocated. When these actions were questioned, the response was defensiveness, leaking of tickets, and, ultimately, language and behavior that go completely against our values and the standards we set for PRP.
      </p>
      <p>
  We know many of you have been waiting for a big vehicle update, but out of respect for the community and to restore trust, we are putting the project on hold until a new team is in place and a fair, community-focused process can be restarted. We’re not here to play favorites or make false promises - we’re here to deliver fun, balanced, and transparent updates for everyone.
      </p>
      <p>
        Thank you to those who brought concerns forward and stood by our standards of respect and accountability. If you ever have questions or see something off, please reach out directly to leadership. We will always listen and protect the integrity of PRP, no matter how tough the call.
      </p>
      <p>
  As part of this transition, we have carefully reviewed custom vehicle allocations. Any vehicles that were added outside the intended process have been removed. If you were affected, please know this is not a reflection on you personally - we know most players simply enjoyed what was given and did nothing wrong. This step is just to ensure a fair and consistent process for everyone moving forward. Thank you for understanding.
      </p>

      <h2>🛠️ Major Refresh Progress & Wipe Talk</h2>
      <p>
  There’s been a lot of rumors - and even leaked clips - about a possible “wipe” coming to PRP. Here’s the truth, directly from us: Yes, leadership has openly discussed a future wipe. BUT, and this is crucial, the only way we move forward with it is when we’re ready to deliver something genuinely new - an actual *refresh* of systems, features, and gameplay. Leadership's current plan favors a single wipe once the major systems (like the inventory) are ready - roughly six months away given current dev capacity. We can run a community vote on timing (or limit the vote to supporters if preferred), but we think telling people they have one solid window to create stories is better than multiple wipe cycles.
      </p>
      <p>
        Too many servers wipe with empty promises or just to “shake things up.” We’ve all seen communities get burned by this, so we’ve kept our cards close until there was real progress. We’ve been grinding behind the scenes: rebuilding the inventory system, laying the foundation for a modern, interactive UI, new jobs, new interaction systems for world objects, and overhauls that’ll change the feel of everyday RP.
      </p>
      <p>
        <b>No wipe will happen until these new systems are finished and worth launching with.</b> We want people to come back to something truly fresh, not just the same city with their stuff deleted.
      </p>
      <p>
        What’s on deck? Here’s what’s already in the pipeline:
      </p>
      <ul>
        <li><b>Completely rewritten inventory</b> (with drag-and-drop, better stacking, weight logic, etc.)</li>
  <li><b>New UI</b> for core systems - modern, fast, and slick (not just a reskin)</li>
  <li><b>New jobs</b> and “job families" - each with their own progression</li>
        <li><b>World interaction system</b> for things like looting, doors, crafting, etc.</li>
        <li>And yes, <b>plenty more sneak peeks</b> to share as features lock in!</li>
      </ul>
      <p>
        <b>TL;DR:</b> We won’t wipe until we’re ready, and you’ll get a full rundown and real teasers first.
      </p>
      <p>
        In the meantime, we’re still pushing out fixes and smaller features while the big stuff is in the oven. Next up for bug fixes: fixing the drug selling issue, ironing out vending machine kinks from the last update, and correcting CAD alert locations in house robbery instances, among others.
      </p>
      <div style="display:flex; flex-direction:column; align-items:center; margin:20px 0;">
        <img src="https://i.gyazo.com/5e35a0e37235c5250153f43b8a31dc33.png" alt="Teaser" style="max-width:700px; width:100%; border-radius:8px; box-shadow:0 2px 8px rgba(0,0,0,0.12);" />
      </div>

      <h2>👮‍♂️ Crime & Cop Balance</h2>
      <p>
        You spoke, and we listened. We’ve adjusted cop-to-crime ratios to reflect our active population. Certain crimes now proceed even if no officers are online, but these are capped per Tsunami. Once police log on, full response mechanics and personal cooldowns come into play.
      </p>

      <h2>🎭 RP First, Always</h2>
      <p>
  We want to remind everyone what sets PRP apart: authentic roleplay. While our population has been smaller lately, we’ve seen a rise in arena-like behavior - openly carrying heavy weapons (C2s and C3s) at all times, always wearing vests, and treating the city like it’s a constant shootout.
      </p>
      <p>
  This isn’t what PRP is about. We are, first and foremost, an RP server. Low-effort and fail RP impacts everyone’s experience. We’ve received a lot of complaints about this lately. Please take a moment to reflect and adjust how you play - give everyone a chance at great stories, not just endless shootouts.
      </p>

      <h2>🚪 New RP Training Instance System</h2>
      <p>
        Due to demand for dedicated development and training environments, and the need to support our Emergency Service Departments in onboarding new recruits quickly, we have created a separate training area that fully separates in-character (IC) and out-of-character (OOC) play. It’s ideal for certifications, drills, and practice without affecting live gameplay. CAD alerts and patrol notifications stay within each instance, while radio communications continue between both.
      </p>
      <ul>
        <li>
          <strong>Access Point:</strong> Phone box in Sandy Shores
          <div style="display:flex; gap:8px; align-items:flex-start; margin:8px 0;">
            <img src="https://i.gyazo.com/d5bd5bbc8db9f6ff5836ffd0e83fc577.jpg" alt="Sandy Shores Phone Booth" style="width:866px; border-radius:4px;" />
          </div>
        </li>
        <li>
          <strong>Access Point:</strong> Phone box near MRPD
          <div style="display:flex; gap:8px; align-items:flex-start; margin:8px 0;">
            <img src="https://i.gyazo.com/f6b57c9c2ab1308f385681e96604b3e0.jpg" alt="MRPD Phone Booth" style="width:866px; border-radius:4px;" />
          </div>
        </li>
        <li><strong>Equipment:</strong> Clock in as staff for the job and use your own gear. Extra items are available by request; do not export them to live play.</li>
        <li><strong>Medical & Revives:</strong> Revives are allowed if staff are present; otherwise, head to the hospital.</li>
        <li><strong>OOC Rules:</strong> No meta, no cross-use. What happens in the OOC instance stays there.</li>
      </ul>
      <p>
        Ideal for training or staff-led sessions. The ESD Discord has already received more information about this.
      </p>

      <h2>👥 Team Update</h2>
      <p>
  We’ve also brought on a familiar face, Margarita, to help with general quality-of-life improvements - everything from the /gloves and /goggles commands to other community-requested tweaks.
      </p>

      <h2>🔧 Bug Fixes & Quality of Life</h2>
      <hr>
      <p>
        <strong>LST Bus Job Fix & Small Buff</strong><br/>
        We resolved the “Waiting for Passengers” UI issue: stops now reliably show passengers boarding and disembarking without delay. Enjoy smoother commutes and no more stuck-in-bus moments.
      </p>

      <p>
        <strong>Bike & Motorcycle Tricks</strong><br/>
        Hold <code>V + Directions (←/↑/↓/→)</code> or <code>V + K</code> for trick animations on bikes and motorbikes. Show off your skills around the city.
      </p>

      <p>
        <strong>9 New Dances Added!</strong><br/>
  Get moving with <b>9 new dances</b> now available using the <code>/dance #</code> command (check the highest numbers - they’re the new ones!). Try them solo or with friends and show off your favorite new moves.
      </p>

      <div style="display: flex; flex-wrap: wrap; gap: 32px; justify-content:center;">
        <div style="flex: 1 1 350px; min-width: 280px; max-width:400px;">
          <div class="video-wrapper" style="position:relative; padding-bottom:56.25%; height:0; overflow:hidden;">
            <iframe src="https://streamable.com/e/0100t0?autoplay=1&muted=1" frameborder="0" allow="autoplay; fullscreen" muted style="position:absolute; top:0; left:0; width:100%; height:100%;"></iframe>
          </div>
          <div style="text-align:center; margin-top:8px;">Bike & Motorcycle Tricks</div>
        </div>
        <div style="flex: 1 1 350px; min-width: 280px; max-width:400px;">
          <div class="video-wrapper" style="position:relative; padding-bottom:56.25%; height:0; overflow:hidden;">
            <iframe src="https://streamable.com/e/ninp29?autoplay=1&muted=1" frameborder="0" allow="autoplay; fullscreen" muted style="position:absolute; top:0; left:0; width:100%; height:100%;"></iframe>
          </div>
          <div style="text-align:center; margin-top:8px;">9 New Dances</div>
        </div>
      </div>

      <h2>🙏 Thanks & What’s Next</h2>
      <p>
  Huge shoutout to everyone providing feedback, testing features, and keeping the roleplay alive - especially our dev and staff volunteers. Your support powers these updates.
      </p>
      <p>
        Stay tuned for next month’s Pulse. Until then, enjoy the new fixes and celebrations! 🎉
      </p>
  <p><strong>- The ProductionRP Team</strong></p>
    `
  },
  {
    id: '2025-05',
    monthLabel: 'May 2025',
    content: `
        <div class="pulse-hero">
          <h1>🔥 BIG ANNOUNCEMENT</h1>
          <p>Let’s talk about the future of PRP.</p>
        </div>
        <div class="pulse-tagline">
          <p>Major features. Community changes. A roadmap to what's next.</p>
        </div>

        <p>
          As many of you know, the staff team had a meeting recently to talk about
          the direction we're heading and how we continue to grow and improve PRP.
        </p>

        <p>
          We want to take a moment to sincerely thank everyone who has donated - not just to the
          server itself, but to the community as a whole. Your continued support means everything,
          and we are truly grateful.
        </p>

  <h2>Passing the Torch - Thank You, Ghost</h2>

        <p>
          We want to take a moment to expulse our deepest gratitude to <strong>Ghost</strong> - someone who helped build ProductionRP from the ground up. Without him, none of this would exist. He started something special, and what began as an idea has become a home for countless players, stories, and friendships.
        </p>

        <p>
          As of this announcement, Ghost has officially stepped down from his ownership position. But this isn’t goodbye - not even close. In fact, Ghost is staying with us as a <strong>main developer</strong>, where he’ll be doing what he’s always done best: building, improving, and evolving the world of PRP behind the scenes.
        </p>

        <p>
          He’s passed the torch to the right people at the right time - people who care deeply about the community and the future. That takes trust, humility, and leadership. We have nothing but respect for the way Ghost handled this transition, and we know he’ll continue to be a huge part of our success.
        </p>

        <p>
          Through the queues and the quiet nights, the highs and the hurdles - PRP has always been a family. And families grow, adapt, and move forward together. This is the next chapter, and we’re proud Ghost will continue to shape it with us, just without the extra admin pulseure on his back.
        </p>

        <h2>Let’s Talk Content</h2>
  
        <p>
          If you’ve been hanging around the supporter chat, you may have caught a little teaser -
          something many of you have been asking for is making a comeback. We’re excited to bring
          this back and improve upon it in the future. While we won’t spoil the surprise just yet,
          know that this is just one step in a larger plan to bring more meaningful updates to PRP.
        </p>

        <p>We’d love to deliver new content all the time, but the reality is:</p>

        <ul>
          <li>Our developers volunteer their time and contribute when they can.</li>
          <li>
            It's tough for devs to stay motivated when the response is mostly toxic responses
            instead of feedback. Such as, laughing at a change, clown emojis or negative comments
            that provide no value. We encourage thoughtful responses and take them into
            consideration.
          </li>
        </ul>

        <p>
          We encourage everyone to revisit the <a href="https://discord.com/channels/888066725923418142/888071546894172240/1365792614208770138" target="_blank" rel="noopener noreferrer">Server Direction post</a> regarding toxicity and negativity. We need to remember: this community isn’t about one individual or group - it’s about all of us.
        </p>

        <p><em>
          With that said again, we recently had some members of the community that ignored the zero
          tolerance for toxic feedback. To live up to our word. Those members have been removed.
        </em></p>

        <p>
          Going forward, content releases will follow a more structured schedule, based on the type
          of content. We’ll be aiming for specific days and times during the week/month. Stay tuned
          for more details.
        </p>

        <h2>So what is dropping now?</h2>
        <p>
          Well how about <strong>Humane Labs!</strong> Many have requested at the very least bring
          back Humane. Well we have listened and Humane 1.1 is here. It has a similar feel to the classic experience plus a few twists.

          Loot pool adjustments/changes for banks and bobcat.
        </p>

        <h2>Quality of Life + Big Projects</h2>

        <p>
          With Ghost, the other owner, stepping down from day-to-day duties to focus more on development,
          we’re making a strong push forward on some long-awaited systems - including the inventory
          and QoL features we’ve teased.
        </p>

        <p>
          Our <strong>clothing team</strong> is working on a nice update for everyone that is tied to the game build update. So eventually when this clothing update is finished, we'll be pushing the build for the server, inducing a outfits reset again when that time comes. (New cars and clothes to play with though, worth)
        </p>

        <p>
          Our <strong>vehicle team</strong> has been highly active and responsive in supporter chats,
          and you’ll continue to see fresh additions hitting the server soon.
        </p>

        <p>
          The <strong>coding team</strong> in general is prioritizing new crime content, addressing bugged civilian
          jobs, the BBB as usual by those specific developers and - if all goes well - working to bring back a version of the 
          <strong>Pacific Standard Bank robbery</strong> as well as several tweaks to existing robberies and their restrictions.
        </p>

        <h3>Vending Machine & Expiration Changes</h3>

        <p>
          After discussions from the recent BBB and follow-up votes, we are planning on adjusting within the next week or so how vending machines and item expiration work.
          These changes aim to reward active food businesses while keeping gameplay fair and flexible.
        </p>

        <ul>
          <li><strong>Vending machines containing food auto-close</strong> when staff are clocked in for food businesses.</li>
          <li><strong>Food & drink expiration extended:</strong> now 4 extra days instead of 2.</li>
          <li><strong>Items must stay in storage for 30 minutes</strong> to lock in the expiration boost.</li>
          <li>Items <strong>sold through stores still keep</strong> the full expiration time.</li>
        </ul>

        <p>
          These systems are meant to encourage more player-to-player interaction and help stocked businesses thrive without relying solely on vending.
        </p>

        <p>
          👉 <em>Full details on this are posted in the BBB discord.</em>
        </p>

        <h2>Inventory Preview</h2>
        <p>Here’s a first look at the upcoming inventory rework and how it’ll feel to interact with if you missed it:</p>
        <div class="video-embed">
          <iframe width="100%" height="400" src="https://www.youtube.com/embed/bp7l1u6VZKQ" title="PRP Inventory Preview" frameborder="0" allowfullscreen></iframe>
        </div>

        <h3 class="sub-heading">Shop UI Sneak Peek from supporter teasers</h3>
        <p>And here’s a quick look at the new shops you'll be interacting with:</p>
        <div class="video-embed">
          <iframe src="https://streamable.com/e/iim312" width="100%" height="400" frameborder="0" allowfullscreen title="Shop UI Preview"></iframe>
        </div>

        <h2>Constructive Suggestions Are Welcome</h2>

        <p>
          We’re always open to suggestions - but please, bring them forward constructively and with
          detail. Avoid vague complaints or personal gripes. Instead, come to us with:
        </p>

        <ul>
          <li>What you’d like to see</li>
          <li>Why you think it benefits the community</li>
          <li>How you imagine it could work</li>
        </ul>

        <p>
          You don’t need to have all the answers, especially when it comes to development
          feasibility - but thoughtful, community-focused input goes a long way.
        </p>

  <h2>Join the Team - Help Us Grow</h2>

        <p>
          If you believe in what we’re building here and want to be a part of it, we’re always looking
          for passionate, driven, and community-minded people to join the team. Whether it's helping on the support side,
          contributing to events, or taking on something bigger - your energy and ideas are what keep PRP alive.
        </p>

        <p>
          We’ve got momentum, we’ve got a plan, and we’ve got room for more people who care. If you're interested in helping us
          shape the future of this community, consider applying below:
        </p>

        <p>
          <a href="https://core.productionrp.org/staffapplication" target="_blank" rel="noopener noreferrer">👉 Apply Here to Join the Staff Team</a>
        </p>
        <p>
          <a href="https://core.productionrp.org/developerapplication" target="_blank" rel="noopener noreferrer">👉 Apply Here to Join the Development Team</a>
        </p>

        <h2>Transparency in Donations</h2>

        <p>
          Many of you have asked for a clearer way to see how we’re doing with server costs.
          The Tebex page now reflects our progress toward the monthly goal for server funding.
          You can check in anytime to see where things stand: <a href="https://store.productionrp.org/" target="_blank" rel="noopener noreferrer">https://store.productionrp.org/</a>
        </p>

        <h2>Huge Thanks to Our Devs & Staff</h2>

        <p>
          We want to close this out with a massive thank you to our developers and staff who
          continue to volunteer their time and energy to build PRP - not just into what it is today,
          but into what we all want it to become.
        </p>

        <p>
          We appreciate every one of you who shows up, participates, and contributes to making this
          community great.
        </p>

        <p><strong>Let’s keep growing together.</strong></p>
  <p><strong>- Kerm & the Administration Team</strong></p>
      `
  }
];
